import { newActivations } from './features/bodycams/activation';
import BodycamPanel, { BodycamIcon } from './features/bodycams/BodycamPanel';
import { useBodycams } from './features/bodycams/useBodycams';
import { siteMarkerPosition, type MarkerEditDraft } from './utils/siteMarkerPosition';
import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useAction, useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { normalizeLatLng } from './utils/geo';
import {
  SITE_TYPES,
  SITE_TYPE_ASSET_NAMES,
  SITE_TYPE_FILTER_OPTIONS,
  getSiteTypeAssetName,
  isSupportedSiteType,
  normalizeSiteTypeLabel,
} from './utils/siteTypes';
import Map from './components/Map';
// import VehicleStatusPanel from './components/VehicleStatusPanel';
import PatrolLogCreator from './components/PatrolLogCreator';
import PatrolReplayPlayer from './components/PatrolReplayPlayer';
import DispatchesPanel from './components/DispatchesPanel';
import VehicleFleetList from './components/VehicleFleetList';
import { selectFleetVehicles } from './lib/vehicleList';
import DispatchReplayPlayer from './components/DispatchReplayPlayer';
import Analytics from './components/Analytics';
import ConfigMigration from './components/ConfigMigration';
import NotificationSystem from './components/NotificationSystem';
import ReportsPage from './components/ReportsPage';
import SiteDetailsPanel from './components/SiteDetailsPanel';
import VehicleAdminSettings from './components/VehicleAdminSettings';
import SiteImageUploader from './components/SiteImageUploader';
import MapAssetsManager from './components/MapAssetsManager';
import VehicleDetailsSidebar from './components/VehicleDetailsSidebar';
import ToursPanel, { type ToursPanelHandle } from './components/ToursPanel';
import EmployeeProfilePanel from './components/EmployeeProfilePanel';
import { UserProfile, UserSecurity, UserSessions, UsersManagement } from '@workos-inc/widgets';
import { SpeedInsights } from '@vercel/speed-insights/react';

const GOOGLE_MAPS_API_KEY = (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
const PLACES_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const PLACES_DETAILS_BASE_URL = 'https://places.googleapis.com/v1/places/';
const ROUTES_COMPUTE_URL = 'https://routes.googleapis.com/directions/v2:computeRoutes';

type PlacesSuggestion = {
  placeId: string;
  mainText: string;
  secondaryText?: string;
  description: string;
};

type AnalyticsHeaderState = {
  title: string;
};

function getCookie(name: string) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1] ?? '') : null;
}

function requireGoogleApiKey() {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error('Google Maps API key not configured');
  }
  return GOOGLE_MAPS_API_KEY;
}

function parseDurationSeconds(duration: unknown): number {
  if (typeof duration === 'number' && Number.isFinite(duration)) return duration;
  if (typeof duration !== 'string') return 0;
  const match = duration.match(/^(\d+)(\.\d+)?s$/);
  if (!match) return 0;
  const seconds = Number(match[1]) + (match[2] ? Number(match[2]) : 0);
  return Number.isFinite(seconds) ? Math.floor(seconds) : 0;
}

function decodePolyline(encoded: string): [number, number][] {
  let index = 0;
  let lat = 0;
  let lng = 0;
  const coordinates: [number, number][] = [];

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let b: number;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dLat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dLat;

    result = 0;
    shift = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dLng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dLng;

    coordinates.push([lng / 1e5, lat / 1e5]);
  }

  return coordinates;
}

function formatDistanceFromMeters(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return '';
  const miles = meters / 1609.344;
  if (miles >= 0.1) {
    const value = miles >= 10 ? miles.toFixed(0) : miles.toFixed(1);
    return `${value} mi`;
  }
  const feet = meters * 3.28084;
  return `${Math.round(feet)} ft`;
}

function createPlacesSessionToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function fetchPlacesAutocomplete(input: string, sessionToken?: string | null): Promise<PlacesSuggestion[]> {
  const apiKey = requireGoogleApiKey();
  const res = await fetch(PLACES_AUTOCOMPLETE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask':
        'suggestions.placePrediction.placeId,' +
        'suggestions.placePrediction.place,' +
        'suggestions.placePrediction.text.text,' +
        'suggestions.placePrediction.structuredFormat.mainText.text,' +
        'suggestions.placePrediction.structuredFormat.secondaryText.text',
    },
    body: JSON.stringify({
      input,
      sessionToken: sessionToken || undefined,
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(`Places autocomplete failed (${res.status})${msg ? `: ${msg}` : ''}`);
  }

  const data = await res.json();
  const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
  return suggestions
    .map((s: any) => s?.placePrediction)
    .filter(Boolean)
    .map((p: any) => {
      const placeId = p?.placeId || (typeof p?.place === 'string' ? p.place.split('/').pop() : '');
      const mainText = p?.structuredFormat?.mainText?.text || p?.text?.text || '';
      const secondaryText = p?.structuredFormat?.secondaryText?.text || '';
      const description = p?.text?.text || `${mainText}${secondaryText ? `, ${secondaryText}` : ''}` || '';
      return {
        placeId,
        mainText,
        secondaryText,
        description,
      } as PlacesSuggestion;
    })
    .filter((p: PlacesSuggestion) => !!p.placeId);
}

async function fetchPlaceDetails(placeId: string, sessionToken?: string | null) {
  const apiKey = requireGoogleApiKey();
  const url = new URL(`${PLACES_DETAILS_BASE_URL}${encodeURIComponent(placeId)}`);
  if (sessionToken) url.searchParams.set('sessionToken', sessionToken);

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': 'formattedAddress,location',
    },
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(`Place details failed (${res.status})${msg ? `: ${msg}` : ''}`);
  }

  const data = await res.json();
  const location = data?.location;
  const lat = location?.latitude;
  const lng = location?.longitude;
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    throw new Error('Place details missing coordinates');
  }

  return {
    address: data?.formattedAddress || placeId,
    lat,
    lng,
  };
}

async function fetchRoute(
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number }
): Promise<{ coordinates: [number, number][]; durationSeconds: number; distanceMeters: number }> {
  const apiKey = requireGoogleApiKey();
  const res = await fetch(ROUTES_COMPUTE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': 'routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline',
    },
    body: JSON.stringify({
      origin: { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } },
      destination: { location: { latLng: { latitude: destination.lat, longitude: destination.lng } } },
      travelMode: 'DRIVE',
      routingPreference: 'TRAFFIC_AWARE',
      computeAlternativeRoutes: false,
    }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(`Routes API failed (${res.status})${msg ? `: ${msg}` : ''}`);
  }

  const data = await res.json();
  const route = Array.isArray(data?.routes) ? data.routes[0] : null;
  if (!route?.polyline?.encodedPolyline) {
    throw new Error('No route found');
  }

  return {
    coordinates: decodePolyline(route.polyline.encodedPolyline),
    durationSeconds: parseDurationSeconds(route.duration),
    distanceMeters: typeof route.distanceMeters === 'number' ? route.distanceMeters : 0,
  };
}

function haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function formatDispatchVehicleLabel(vehicleName?: string, driverName?: string) {
  const base = (vehicleName || "").trim();
  const driver = (driverName || "").trim();
  if (driver) return base ? `${base} (${driver})` : driver;
  return base || "Vehicle";
}

function sanitizeGuardLabel(guardName?: string) {
  const guard = (guardName || "").trim();
  if (!guard) return "";
  const lowered = guard.toLowerCase();
  if (lowered === "unknown" || lowered === "unknown guard") return "";
  if (/^guard\s+\d+$/i.test(guard)) return "";
  if (guard.includes("visit.guardName") || guard.includes("||")) return "";
  return guard;
}

function formatPatrolCardLabel(vehicleName?: string, guardName?: string) {
  const guard = sanitizeGuardLabel(guardName);
  if (guard) return guard;
  const vehicle = (vehicleName || "").trim();
  return vehicle || "Vehicle";
}

function formatDurationCompactMs(durationMs: number | null | undefined): string {
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs) || durationMs <= 0) return "-";
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatEventTimestamp(ts: number): string {
  if (!Number.isFinite(ts) || ts <= 0) return "-";
  return new Date(ts).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function mergeUniqueByKey<T>(previous: T[], incoming: T[], getKey: (row: T) => string): T[] {
  const merged = new globalThis.Map<string, T>();
  for (const row of previous || []) {
    const key = getKey(row);
    if (!key) continue;
    merged.set(key, row);
  }
  for (const row of incoming || []) {
    const key = getKey(row);
    if (!key) continue;
    merged.set(key, row);
  }
  return Array.from(merged.values());
}

function maxNumericField(rows: any[] | undefined, field: string): number {
  let max = 0;
  for (const row of rows || []) {
    const value = row?.[field];
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    if (value > max) max = value;
  }
  return max;
}

const HOME_EVENTS_CACHE_KEY = "home-events-cache-v4";

// Icon components for the sidebar
const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
  </svg>
);

const VehicleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
  </svg>
);

const SiteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);

const PatrolIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
  </svg>
);

const DispatchIcon = () => (
  <svg className="dispatch-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9,2C8.45,2,8,2.45,8,3c0,5.67,0,11.33,0,17c0,1.11,0.89,2,2,2h5c1.11,0,2-0.89,2-2V9c0-1.11-0.89-2-2-2h-5V3C10,2.45,9.55,2,9,2 M10,9h5v4h-5V9z" />
  </svg>
);

const AlertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
  </svg>
);

const ToursIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="6" cy="19" r="3" fill="currentColor" stroke="none"/>
    <circle cx="18" cy="5" r="3" fill="currentColor" stroke="none"/>
    <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/>
  </svg>
);

// TRACKTIK REPORTS DISABLED - icon kept for when reports are re-enabled.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ReportsIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h9l4 4v14H6z" />
    <path d="M15 3v5h4" />
    <path d="M9 12h6" />
    <path d="M9 16h6" />
    <path d="M9 8h2" />
  </svg>
);

// TRACKTIK REPORTS DISABLED - icon kept for when reports are re-enabled.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _AssignmentsIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5h16" />
    <path d="M4 12h16" />
    <path d="M4 19h16" />
    <path d="M8 3v4" />
    <path d="M14 10v4" />
    <path d="M18 17v4" />
  </svg>
);

// Keep the disabled report icons referenced so noUnusedLocals stays happy.
void _ReportsIcon;
void _AssignmentsIcon;

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
  </svg>
);

function SidebarPanel({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 40);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`sidebar-panel${mounted ? ' sidebar-panel--mounted' : ''}`}>
      {children}
    </div>
  );
}

type RspndrTarget = {
  address: string;
  lat: number;
  lng: number;
  placeId?: string;
};

function FocusedDispatchRouteSync(props: {
  focusedDispatchId: any;
  sites: any[] | undefined;
  dispatchResult: any;
  isRouteConfirmed: boolean;
  isSuppressed: boolean;
  setDispatchResult: (v: any) => void;
  setIsRouteConfirmed: (v: boolean) => void;
  setDispatchStartedAt: (v: number | null) => void;
  setActiveDispatchSiteId: (v: any) => void;
  setMapSelectedSiteId: (v: any) => void;
  setRspndrTarget: (v: RspndrTarget | null) => void;
}) {
  const focusedDispatch = useQuery(api.dispatches.getById, { dispatchId: props.focusedDispatchId }) as any;
  const focusedDispatchPoints = useQuery(api.dispatches.getPoints, {
    dispatchId: props.focusedDispatchId,
    limit: 5000,
  }) as any[] | undefined;

  // When the backend transitions away from traveling, the "traveling" UI + route overlay
  // should go away (navigation is complete once the vehicle enters the site geofence).
  useEffect(() => {
    const status = focusedDispatch?.status as string | undefined;
    if (!status) return;

    if (status === "traveling") return;

    // For on_site/completed/cancelled: clear the route overlay if it's currently showing.
    if (props.isRouteConfirmed) {
      props.setIsRouteConfirmed(false);
      props.setDispatchResult(null);
      props.setDispatchStartedAt(null);
    }

    // Keep the destination highlighted while on-site (useful context).
    if (focusedDispatch?.dispatchType === "rspndr") {
      const lat = (focusedDispatch as any)?.targetLatitude;
      const lng = (focusedDispatch as any)?.targetLongitude;
      if (typeof lat === "number" && typeof lng === "number") {
        props.setRspndrTarget({
          address: (focusedDispatch as any)?.targetAddress || focusedDispatch.siteName || "RSPNDR Dispatch",
          lat,
          lng,
        });
      } else {
        props.setRspndrTarget(null);
      }
      props.setActiveDispatchSiteId(null);
      props.setMapSelectedSiteId(null);
    } else if (focusedDispatch?.siteId) {
      props.setActiveDispatchSiteId(focusedDispatch.siteId);
      props.setMapSelectedSiteId(focusedDispatch.siteId);
      props.setRspndrTarget(null);
    } else {
      props.setRspndrTarget(null);
    }
  }, [focusedDispatch?.status, focusedDispatch?.siteId, focusedDispatch?.dispatchType, props]);

  const derived = useMemo(() => {
    if (!focusedDispatch) return null;
    const isRspndr = focusedDispatch?.dispatchType === "rspndr";
    let target: { lat: number; lng: number } | null = null;
    let siteName = focusedDispatch.siteName;

    if (isRspndr) {
      const lat = (focusedDispatch as any)?.targetLatitude;
      const lng = (focusedDispatch as any)?.targetLongitude;
      if (typeof lat === "number" && typeof lng === "number") target = { lat, lng };
    } else {
      if (!props.sites || props.sites.length === 0) return null;
      const site = props.sites.find((s: any) => s._id === focusedDispatch.siteId);
      if (!site) return null;
      if (typeof site.longitude === "number" && typeof site.latitude === "number") {
        target = { lat: site.latitude, lng: site.longitude };
      }
      siteName = site?.name || focusedDispatch.siteName;
    }
    if (!target) return null;

    // Prefer the persisted planned route (Google Directions) if available.
    const persisted = Array.isArray(focusedDispatch.routeCoordinates) ? focusedDispatch.routeCoordinates : null;
    let coords: [number, number][] = [];
    if (persisted && persisted.length >= 2) {
      coords = persisted
        .map((c: any) => [c?.[0], c?.[1]] as [number, number])
        .filter((c: [number, number]) => typeof c[0] === "number" && typeof c[1] === "number");
    } else if (Array.isArray(focusedDispatchPoints)) {
      // Fallback: GPS trail + destination point (better than nothing).
      coords = focusedDispatchPoints
        .map((p: any) => [p.longitude, p.latitude] as [number, number])
        .filter((c) => typeof c[0] === "number" && typeof c[1] === "number");
    }

    // Extend to destination so the line always points "to the site"
    coords.push([target.lng, target.lat]);

    if (coords.length < 2) return null;

    return {
      siteName,
      dispatchType: focusedDispatch.dispatchType,
      optimalVehicle: {
        vehicleId: focusedDispatch.vehicleDocId,
        vehicleName: focusedDispatch.vehicleName,
        driverName: focusedDispatch.driverName,
        eta: focusedDispatch.routeEta || "",
        duration: focusedDispatch.routeDurationText || "",
        distance: focusedDispatch.routeDistanceText || "",
        _durationSeconds: focusedDispatch.routeDurationSeconds,
        route: { coordinates: coords },
      },
      allOptions: [],
    };
  }, [focusedDispatch, focusedDispatchPoints, props.sites]);

  useEffect(() => {
    if (props.isSuppressed) return;
    const status = focusedDispatch?.status;
    const isActive = status === "traveling" || status === "on_site";
    if (!isActive) return;
    // Only show the route overlay while traveling.
    if (status !== "traveling") return;
    if (!derived) return;

    // Don't override the "pre-OK" local dispatch selection UI.
    if (props.dispatchResult && !props.isRouteConfirmed) return;
    // Only restore once (post-refresh). If the user already has an active overlay,
    // don't keep overwriting it as new points arrive.
    if (props.isRouteConfirmed) return;

    props.setDispatchResult(derived);
    props.setIsRouteConfirmed(true);
    if (typeof focusedDispatch?.startedAt === "number") props.setDispatchStartedAt(focusedDispatch.startedAt);
    if (focusedDispatch?.dispatchType === "rspndr") {
      const lat = (focusedDispatch as any)?.targetLatitude;
      const lng = (focusedDispatch as any)?.targetLongitude;
      if (typeof lat === "number" && typeof lng === "number") {
        props.setRspndrTarget({
          address: (focusedDispatch as any)?.targetAddress || focusedDispatch.siteName || "RSPNDR Dispatch",
          lat,
          lng,
        });
      } else {
        props.setRspndrTarget(null);
      }
      props.setActiveDispatchSiteId(null);
      props.setMapSelectedSiteId(null);
    } else {
      props.setActiveDispatchSiteId(focusedDispatch.siteId);
      props.setMapSelectedSiteId(focusedDispatch.siteId);
      props.setRspndrTarget(null);
    }
  }, [focusedDispatch, derived, props]);

  return null;
}

type AppProps = {
  sessionDisplayName: string;
  sessionEmail: string;
  onSignOut: () => Promise<void> | void;
};

type ManageAccountsPanel = 'members' | 'profile' | 'security' | 'sessions';

type HomeEventType =
  | "entry"
  | "exit"
  | "double_hit"
  | "quick_patrol"
  | "checkpoint_scan"
  | "checkpoint_rule";

type EventsCenterEventTypeFilter = "all" | HomeEventType;

const HOME_EVENT_TIE_BREAK_PRIORITY: Record<HomeEventType, number> = {
  entry: 0,
  checkpoint_scan: 1,
  checkpoint_rule: 2,
  double_hit: 3,
  exit: 4,
  quick_patrol: 5,
};

const EVENTS_CENTER_EVENT_TYPE_OPTIONS: Array<{ value: EventsCenterEventTypeFilter; label: string }> = [
  { value: "all", label: "All Events" },
  { value: "entry", label: "Site Entry" },
  { value: "checkpoint_scan", label: "Checkpoint Scan" },
  { value: "checkpoint_rule", label: "Checkpoint Rule" },
  { value: "double_hit", label: "Double Hit" },
  { value: "exit", label: "Site Exit" },
  { value: "quick_patrol", label: "Quick Patrol" },
];

function EventTypeDropdown({
  value,
  onChange,
  options,
}: {
  value: EventsCenterEventTypeFilter;
  onChange: (val: EventsCenterEventTypeFilter) => void;
  options: Array<{ value: EventsCenterEventTypeFilter; label: string }>;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  return (
    <div className="ec-dropdown" ref={containerRef}>
      <button
        type="button"
        className={`ec-dropdown-trigger${open ? " ec-dropdown-trigger--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter events by type"
      >
        <span className="ec-dropdown-trigger-label">{selected.label}</span>
        <svg
          className="ec-dropdown-chevron"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <ul className="ec-dropdown-panel" role="listbox" aria-label="Filter events by type">
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`ec-dropdown-option${opt.value === value ? " ec-dropdown-option--active" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <span className="ec-dropdown-option-indicator" aria-hidden="true" />
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SITE_TYPE_OPTIONS: Array<{ value: string; label: string }> = SITE_TYPE_FILTER_OPTIONS;

function SiteTypeDropdown({
  value,
  onChange,
}: {
  value: string;
  onChange: (val: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = SITE_TYPE_OPTIONS.find((o) => o.value === value) ?? SITE_TYPE_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  return (
    <div className="ec-dropdown" ref={containerRef}>
      <button
        type="button"
        className={`ec-dropdown-trigger${open ? " ec-dropdown-trigger--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter sites by type"
      >
        <span className="ec-dropdown-trigger-label">{selected.label}</span>
        <svg
          className="ec-dropdown-chevron"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <ul className="ec-dropdown-panel" role="listbox" aria-label="Filter sites by type">
          {SITE_TYPE_OPTIONS.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`ec-dropdown-option${opt.value === value ? " ec-dropdown-option--active" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <span className="ec-dropdown-option-indicator" aria-hidden="true" />
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const VALID_SITE_TYPE_FILTERS = new Set(["all", "archived", ...SITE_TYPES]);

type HomeEventRow = {
  key: string;
  type: HomeEventType;
  timestamp: number;
  siteName: string;
  guardLabel: string;
  detail: string;
  vehicleLabel: string | null;
  quickPatrolVisitId: string | null;
  exitVehicleId: string | null;
  exitSiteId: string | null;
  checkpointRuleName?: string;
  checkpointRuleLabel?: string;
};

type HomeActiveTourSession = {
  tourSessionId: number;
  tourName?: string;
  employeeName?: string;
  startTime?: number | string;
  endTime?: number | string | null;
  duration?: number;
  status?: string;
  totalScans?: number;
  totalCheckpointsInTour?: number;
  completionPercentage?: number;
  scans?: Array<{ checkpointId?: number | null }>;
};

type HomePatrolCardRow = {
  key: string;
  guardLabel: string;
  vehicle: any | null;
  tour: HomeActiveTourSession | null;
  hitCount: number;
  completionPct: number;
  startedAtMs: number;
};

function normalizeSiteNameForMatchClient(name: string): string {
  return (name || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[:\-]\s*\d+$/, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function stripCheckpointSuffixClient(name: string): string {
  return (name || "").replace(/[:\-]\s*\d+\s*$/, "").trim();
}

function checkpointLabelMatchesSiteNameClient(checkpointLabel: string, siteName: string): boolean {
  const normalizedSite = normalizeSiteNameForMatchClient(siteName);
  if (!normalizedSite) return false;

  const normalizedCheckpoint = normalizeSiteNameForMatchClient(checkpointLabel);
  if (normalizedCheckpoint && (normalizedCheckpoint === normalizedSite || normalizedCheckpoint.includes(normalizedSite) || normalizedSite.includes(normalizedCheckpoint))) {
    return true;
  }

  const checkpointBase = normalizeSiteNameForMatchClient(stripCheckpointSuffixClient(checkpointLabel));
  if (checkpointBase && (checkpointBase === normalizedSite || checkpointBase.includes(normalizedSite) || normalizedSite.includes(checkpointBase))) {
    return true;
  }

  return false;
}

function resolveCheckpointScanSiteNameForDisplay(args: {
  rawSiteName: string;
  rawCheckpointName: string;
  candidateSites: string[];
}): string {
  const sourceLabels = [args.rawSiteName, args.rawCheckpointName]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  if (sourceLabels.length === 0) return "Unknown Site";

  for (const label of sourceLabels) {
    const exact = args.candidateSites.find(
      (site) => normalizeSiteNameForMatchClient(site) === normalizeSiteNameForMatchClient(label)
    );
    if (exact) return exact;
  }

  for (const label of sourceLabels) {
    const matched = args.candidateSites.find((site) => checkpointLabelMatchesSiteNameClient(label, site));
    if (matched) return matched;
  }

  const stripped = stripCheckpointSuffixClient(sourceLabels[0]);
  return stripped || sourceLabels[0];
}

function App({ sessionDisplayName, sessionEmail, onSignOut }: AppProps) {
  const [activeTab, setActiveTab] = useState('home');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [siteModelFocusRequest, setSiteModelFocusRequest] = useState<{ siteId: string; sequence: number } | null>(null);
  const [isCreatingSite, setIsCreatingSite] = useState(false);
  const [siteContextMenu, setSiteContextMenu] = useState<{
    x: number;
    y: number;
    siteId: string;
    mode: 'active' | 'archived';
  } | null>(null);
  // Used only to highlight a site marker on the map (does NOT open SiteDetailsPanel)
  const [mapSelectedSiteId, setMapSelectedSiteId] = useState<string | null>(null);
  // Locked destination for an active dispatch (so clicking other sites doesn't retarget routing).
  const [activeDispatchSiteId, setActiveDispatchSiteId] = useState<string | null>(null);
  const [dispatchResult, setDispatchResult] = useState<any | null>(null);
  const [dispatchOptionIndex, setDispatchOptionIndex] = useState(0);
  const [isRouteConfirmed, setIsRouteConfirmed] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [isRecalculatingRoute, setIsRecalculatingRoute] = useState(false);
  const [recalculatingLabel, setRecalculatingLabel] = useState<string | null>(null);
  const [dispatchStartedAt, setDispatchStartedAt] = useState<number | null>(null);
  const [travelNowMs, setTravelNowMs] = useState<number>(() => Date.now());
  const [isDispatchMenuOpen, setIsDispatchMenuOpen] = useState(false);
  const dispatchMenuRef = useRef<HTMLDivElement | null>(null);
  const [backgroundDispatches, setBackgroundDispatches] = useState<any[]>([]);
  const [isDispatchOverlaySuppressed, setIsDispatchOverlaySuppressed] = useState(false);
  const [pendingDispatchSite, setPendingDispatchSite] = useState<any | null>(null);
  const [pendingRspndrTarget, setPendingRspndrTarget] = useState<RspndrTarget | null>(null);
  const [showDispatchSwitchModal, setShowDispatchSwitchModal] = useState(false);
  const recalcRequestIdRef = useRef(0);
  const lastAutoRouteOriginRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastAutoRouteRecalcAtRef = useRef<number>(0);
  const [mapSiteSearch, setMapSiteSearch] = useState('');
  const [mapSearchMode, setMapSearchMode] = useState<'sites' | 'rspndr'>('sites');
  const [rspndrSuggestions, setRspndrSuggestions] = useState<PlacesSuggestion[]>([]);
  const [rspndrTarget, setRspndrTarget] = useState<RspndrTarget | null>(null);
  const [rspndrSearchError, setRspndrSearchError] = useState<string | null>(null);
  const rspndrSearchSeqRef = useRef(0);
  const rspndrSessionTokenRef = useRef<string | null>(null);
  const [mapDispatchError, setMapDispatchError] = useState<string | null>(null);
  const [isMapSiteDropdownOpen, setIsMapSiteDropdownOpen] = useState(false);
  const mapSiteDropdownCloseTimerRef = useRef<number | null>(null);
  const mapSearchInputRef = useRef<HTMLInputElement | null>(null);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const [showPatrolLogModal, setShowPatrolLogModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showReplayModal, setShowReplayModal] = useState(false);
  const [showManageAccountsModal, setShowManageAccountsModal] = useState(false);
  const [manageAccountsPanel, setManageAccountsPanel] = useState<ManageAccountsPanel>('members');
  const [manageAccountsError, setManageAccountsError] = useState<string | null>(null);
  const [manageAccountsMembersToken, setManageAccountsMembersToken] = useState<string | null>(null);
  const [isManageAccountsMembersLoading, setIsManageAccountsMembersLoading] = useState(false);
  const [manageAccountsMembersRetryKey, setManageAccountsMembersRetryKey] = useState(0);
  const [selectedPatrolVisit, setSelectedPatrolVisit] = useState<any>(null);
  const [homeQuickReplayLoadingVisitId, setHomeQuickReplayLoadingVisitId] = useState<string | null>(null);
  const [exitReplayLoadingKey, setExitReplayLoadingKey] = useState<string | null>(null);
  const [homeEventsReady, setHomeEventsReady] = useState(false);
  const [homeEventsHydrated, setHomeEventsHydrated] = useState(false);
  const [homeEventsCursorTime, setHomeEventsCursorTime] = useState<number | null>(null); // write-time cursor (updatedAt)
  const [homeEventsCache, setHomeEventsCache] = useState<any[]>([]);
  const [eventsCenterLimit, setEventsCenterLimit] = useState(100);
  const [eventsCenterSearchInput, setEventsCenterSearchInput] = useState("");
  const [eventsCenterSearch, setEventsCenterSearch] = useState("");
  const [eventsCenterTypeFilter, setEventsCenterTypeFilter] = useState<EventsCenterEventTypeFilter>("all");
  const [analyticsHeaderTitle, setAnalyticsHeaderTitle] = useState('Analytics');
  const [analyticsSidebarBackNonce, setAnalyticsSidebarBackNonce] = useState(0);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [showMapAssetsManager, setShowMapAssetsManager] = useState(false);
  const [hideSites, setHideSites] = useState(false);
  const [showGeofences, setShowGeofences] = useState(true);
  const [geofenceWarmReady, setGeofenceWarmReady] = useState(false);
  const [markerEditorEnabled, setMarkerEditorEnabled] = useState(false);
  const [markerEditDraft, setMarkerEditDraft] = useState<MarkerEditDraft | null>(null);
  const [markerEditSaving, setMarkerEditSaving] = useState(false);
  const [markerEditError, setMarkerEditError] = useState<string | null>(null);
  const markerSavePendingRef = useRef(false);
  const [geofenceEditorEnabled, setGeofenceEditorEnabled] = useState(false);
  const [mapStyleMode, setMapStyleMode] = useState<'standard' | 'satellite'>('standard');

  // Geofence editor
  const [geofenceEditSiteId, setGeofenceEditSiteId] = useState<string | null>(null);
  const geofenceDraftRingRef = useRef<[number, number][] | null>(null);
  const [geofenceSaveError, setGeofenceSaveError] = useState<string | null>(null);

  // Dispatches (global)
  const [focusedDispatchId, setFocusedDispatchId] = useState<any>(null);
  const [replayDispatchId, setReplayDispatchId] = useState<any>(null);
  
  // Patrol filters
  const [patrolSearchQuery, setPatrolSearchQuery] = useState('');
  const [patrolDisplayLimit, setPatrolDisplayLimit] = useState(25);
  const contentBodyRef = useRef<HTMLDivElement | null>(null);
  const toursPanelRef = useRef<ToursPanelHandle | null>(null);
  const patrolScrollRestoreTopRef = useRef<number | null>(null);
  const lastPatrolVisitsRef = useRef<any[] | null>(null);
  
  // Site filters
  const [siteTypeFilter, setSiteTypeFilter] = useState<string>('all');
  const [siteSearchQuery, setSiteSearchQuery] = useState('');

  // Vehicle filters
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  
  // NOTE: dispatch geofence parsing is handled on the backend now.

  const formatElapsed = useCallback((ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad2 = (n: number) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
  }, []);

  const haversineMiles = useCallback((a: [number, number], b: [number, number]) => {
    // a/b are [lng,lat]
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 3959; // miles
    const dLat = toRad(b[1] - a[1]);
    const dLng = toRad(b[0] - a[0]);
    const lat1 = toRad(a[1]);
    const lat2 = toRad(b[1]);
    const s1 = Math.sin(dLat / 2);
    const s2 = Math.sin(dLng / 2);
    const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }, []);

  const polylineMiles = useCallback(
    (coords: [number, number][]) => {
      if (!coords || coords.length < 2) return 0;
      let sum = 0;
      for (let i = 1; i < coords.length; i++) sum += haversineMiles(coords[i - 1], coords[i]);
      return sum;
    },
    [haversineMiles]
  );

  const formatDurationFromSeconds = useCallback((seconds: number) => {
    const s = Math.max(0, Math.floor(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h} hr ${m} min`;
    return `${m} min`;
  }, []);

  const fetchWorkOsWidgetToken = useCallback(async (scopes?: string[]) => {
    const csrfToken = getCookie('csrfToken');
    const response = await fetch('/api/auth/widgets-token', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      body: JSON.stringify({
        scopes: Array.isArray(scopes) ? scopes : [],
      }),
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || typeof payload?.authToken !== 'string' || !payload.authToken) {
      const message =
        typeof payload?.error === 'string' ? payload.error : 'Unable to open account management right now.';
      setManageAccountsError(message);
      throw new Error(message);
    }

    setManageAccountsError(null);
    return payload.authToken;
  }, []);

  const fetchWorkOsUsersManagementToken = useCallback(
    () => fetchWorkOsWidgetToken(['widgets:users-table:manage']),
    [fetchWorkOsWidgetToken]
  );

  useEffect(() => {
    if (!showManageAccountsModal) {
      setManageAccountsError(null);
      setManageAccountsMembersToken(null);
      setIsManageAccountsMembersLoading(false);
      return;
    }
    if (manageAccountsPanel !== 'members') {
      return;
    }

    let cancelled = false;
    setIsManageAccountsMembersLoading(true);

    fetchWorkOsUsersManagementToken()
      .then((authToken) => {
        if (cancelled) return;
        setManageAccountsMembersToken(authToken);
        setManageAccountsError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Unable to open account management right now.';
        setManageAccountsMembersToken(null);
        setManageAccountsError(message);
      })
      .finally(() => {
        if (cancelled) return;
        setIsManageAccountsMembersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    fetchWorkOsUsersManagementToken,
    manageAccountsMembersRetryKey,
    manageAccountsPanel,
    showManageAccountsModal,
  ]);

  // NOTE: point-in-polygon for dispatch stage transitions is handled on the backend now.

  const endActiveDispatch = useCallback((reason: 'cancelled' | 'arrived') => {
    // Reason is currently for future UX; keep for debugging. [[memory:2972400]]
    console.log(`[DISPATCH-END] reason=${reason}`);
    setIsDispatchOverlaySuppressed(false);
    setIsRouteConfirmed(false);
    setDispatchStartedAt(null);
    setIsDispatchMenuOpen(false);
    setDispatchResult(null);
    setMapSelectedSiteId(null);
    setActiveDispatchSiteId(null);
    setRspndrTarget(null);
    setDispatchOptionIndex(0);
  }, []);

  const startGeofenceEditForSiteId = useCallback((siteId: string) => {
    console.log('[App] startGeofenceEditForSiteId called with:', siteId);
    if (!siteId) {
      console.log('[App] ERROR: No siteId provided');
      setGeofenceSaveError("GEOFENCE-UI-001 No selected site");
      return;
    }
    console.log('[App] Setting geofenceEditSiteId to:', siteId);
    setGeofenceSaveError(null);
    geofenceDraftRingRef.current = null;
    setGeofenceEditSiteId(siteId);
    // Clicking a geofence while the editor is enabled should not open the site details card.
    setSelectedSite(null);
    setMapSelectedSiteId(siteId);
    console.log('[App] Edit session started for site:', siteId);
  }, []);

  const cancelGeofenceEdit = useCallback(() => {
    setGeofenceEditSiteId(null);
    geofenceDraftRingRef.current = null;
    setGeofenceSaveError(null);
  }, []);

  const handleGeofenceEditChange = useCallback((ring: [number, number][]) => {
    geofenceDraftRingRef.current = ring;
  }, []);

  // If editor mode is enabled, geofences must be visible to click-edit them.
  useEffect(() => {
    if (!geofenceEditorEnabled) return;
    if (!showGeofences) setShowGeofences(true);
  }, [geofenceEditorEnabled, showGeofences]);

  useEffect(() => {
    const timer = window.setTimeout(() => setGeofenceWarmReady(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  // Defer home event queries briefly so map/assets can render first after hard refresh.
  useEffect(() => {
    if (activeTab !== 'home') return;
    if (homeEventsReady) return;
    const timer = window.setTimeout(() => setHomeEventsReady(true), 150);
    return () => window.clearTimeout(timer);
  }, [activeTab, homeEventsReady]);

  // Warm home event cache from localStorage so Home can render immediately on return/refresh.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HOME_EVENTS_CACHE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        cursorTime?: number;
        rows?: any[];
      };
      const rows = Array.isArray(parsed?.rows) ? parsed.rows.slice(0, 300) : [];
      if (rows.length === 0) return;

      setHomeEventsCache(rows);
      setHomeEventsHydrated(true);

      const cursorCandidate =
        typeof parsed?.cursorTime === "number" && Number.isFinite(parsed.cursorTime)
          ? parsed.cursorTime
          : maxNumericField(rows, "updatedAt");
      if (cursorCandidate > 0) setHomeEventsCursorTime(cursorCandidate);
      setHomeEventsReady(true);
    } catch {
      // Ignore parse/storage errors.
    }
  }, []);

  useEffect(() => {
    if (!homeEventsHydrated) return;
    try {
      localStorage.setItem(
        HOME_EVENTS_CACHE_KEY,
        JSON.stringify({
          cursorTime: homeEventsCursorTime || 0,
          rows: homeEventsCache.slice(0, 300),
        })
      );
    } catch {
      // Ignore storage errors.
    }
  }, [
    homeEventsHydrated,
    homeEventsCursorTime,
    homeEventsCache,
  ]);

  // Queries
  const vehiclesLite = useQuery((api as any).vehicles.getLive);
  const vehiclesFull = useQuery(
    api.vehicles.getAll,
    activeTab === 'vehicles' ? {} : ("skip" as any)
  );
  const vehicles = useMemo(() => vehiclesFull ?? vehiclesLite, [vehiclesFull, vehiclesLite]);

  const sitesLite = useQuery((api as any).sites.getActiveBasic);
  const sitesWithImages = useQuery(
    api.sites.getActive,
    activeTab === 'sites' ? {} : ("skip" as any)
  );
  const sites = useMemo(() => sitesWithImages ?? sitesLite, [sitesWithImages, sitesLite]);

  // Stable geofence string for the active replay site.
  // Keyed on the raw string so the parsed coords array only gets a new reference
  // when the geofence data actually changes — prevents map reinit on Convex re-renders.
  const replayGeoFenceStr = useMemo(() => {
    if (!selectedPatrolVisit?.siteName || !sites) return '';
    return (
      (sites as any[]).find((s: any) => s.name === selectedPatrolVisit.siteName)
        ?.geoFenceCoordinates ?? ''
    );
  }, [selectedPatrolVisit?.siteName, sites]);

  const replayGeofenceCoords = useMemo(() => {
    if (!replayGeoFenceStr) return null;
    try {
      const coords = JSON.parse(replayGeoFenceStr);
      if (coords && Array.isArray(coords[0])) return coords as number[][][];
    } catch {}
    return null;
  }, [replayGeoFenceStr]);

  const archivedSites = useQuery(
    api.sites.getArchived,
    activeTab === 'sites' ? {} : ("skip" as any)
  );
  const profile = useQuery((api as any).userProfiles.getMyProfile, {
    fallbackName: sessionDisplayName || undefined,
    fallbackEmail: sessionEmail || undefined,
  }) as any;
  const syncMyProfileIdentity = useMutation((api as any).userProfiles.syncMyProfileIdentity);
  const activeDispatches = useQuery(api.dispatches.getActive);
  const dispatchHistory = useQuery(api.dispatches.getHistory, { limit: 50 });
  const coreAssets = useQuery(api.appAssets.getByNames, {
    names: [
      'Vehicle_IsMoving',
      'Vehicle_NotMoving',
      'SiteIcon',
      'patrol_vehicle',
      ...SITE_TYPE_ASSET_NAMES,
    ],
  });
  const layerThumbAssets = useQuery(
    api.appAssets.getByNames,
    {
      names: [
        'LayerThumb_Dawn',
        'LayerThumb_Day',
        'LayerThumb_Dusk',
        'LayerThumb_Night',
        'LayerThumb_Satellite',
      ],
    }
  );
  const appAssets = useMemo(
    () => ({
      ...(coreAssets || {}),
      ...(layerThumbAssets || {}),
    }),
    [coreAssets, layerThumbAssets]
  );
  const mapDisplaySettings = useQuery((api as any).appSettings.getMapDisplaySettings) as
    | { siteMarkerSizePx?: number; siteMarkerSizes?: Record<string, number> }
    | undefined;
  const mapAssetUrls = useMemo(
    () => ({
      Vehicle_IsMoving: appAssets?.Vehicle_IsMoving || null,
      Vehicle_NotMoving: appAssets?.Vehicle_NotMoving || null,
      SiteIcon: appAssets?.SiteIcon || null,
      SiteIconsByType: Object.fromEntries(
        SITE_TYPES.map((siteType) => [siteType, appAssets?.[getSiteTypeAssetName(siteType)] || null])
      ) as Record<string, string | null>,
    }),
    [appAssets]
  );
  const mapLayerThumbnails = useMemo(
    () => ({
      dawn: appAssets?.LayerThumb_Dawn || null,
      day: appAssets?.LayerThumb_Day || null,
      dusk: appAssets?.LayerThumb_Dusk || null,
      night: appAssets?.LayerThumb_Night || null,
      satellite: appAssets?.LayerThumb_Satellite || null,
    }),
    [appAssets]
  );

  
  const patrolSearchTrimmed = patrolSearchQuery.trim();
  const patrolSearchLower = patrolSearchTrimmed.toLowerCase();
  const patrolQuerySiteName = useMemo(() => {
    if (!patrolSearchLower || !Array.isArray(sites) || sites.length === 0) return null;
    const exact = sites.find((site: any) => (site?.name || "").trim().toLowerCase() === patrolSearchLower);
    if (exact?.name) return exact.name as string;

    const partialMatches = sites.filter((site: any) =>
      (site?.name || "").trim().toLowerCase().includes(patrolSearchLower)
    );
    if (partialMatches.length === 1 && partialMatches[0]?.name) return partialMatches[0].name as string;
    return null;
  }, [patrolSearchLower, sites]);
  const patrolQueryLimit = patrolSearchTrimmed ? Math.max(100, patrolDisplayLimit) : patrolDisplayLimit;
  const patrolLoadStep = patrolSearchTrimmed ? 100 : 50;

  // Load patrols with server-side pagination.
  const patrolVisits = useQuery(
    api.patrolVisits.getRecentPatrolVisits,
    activeTab === 'patrols'
      ? (patrolQuerySiteName
          ? { limit: patrolQueryLimit, siteName: patrolQuerySiteName }
          : { limit: patrolQueryLimit })
      : ("skip" as any)
  );
  const recentPatrolVisitsForExitReplay = useQuery(
    api.patrolVisits.getRecentPatrolVisits,
    (activeTab === "alerts" || activeTab === "home") ? { limit: 900 } : ("skip" as any)
  ) as any[] | undefined;
  const patrolVisitsData = patrolVisits ?? lastPatrolVisitsRef.current;
  const homeEventsEnabled = activeTab === 'home' && homeEventsReady;
  const homeEventsSinceTime =
    homeEventsHydrated &&
    typeof homeEventsCursorTime === "number" &&
    Number.isFinite(homeEventsCursorTime)
      ? Math.max(0, homeEventsCursorTime - 1000)
      : undefined;
  const homeEventsFeedRows = useQuery(
    (api as any).homeEvents.getRecentHomeEvents,
    homeEventsEnabled ? { limit: 80, sinceTime: homeEventsSinceTime } : ("skip" as any)
  ) as any[] | undefined;
  const eventsCenterRows = useQuery(
    (api as any).homeEvents.getRecentHomeEvents,
    activeTab === "alerts"
      ? {
          limit: eventsCenterLimit,
          eventType: eventsCenterTypeFilter === "all" ? undefined : eventsCenterTypeFilter,
          search: eventsCenterSearch || undefined,
        }
      : ("skip" as any)
  ) as any[] | undefined;
  const checkpointDisplayNamesRaw = useQuery(
    (api as any).checkpointRules.getCheckpointDisplayNames
  ) as { checkpointId: number; displayName: string }[] | undefined;
  const checkpointDisplayNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    for (const entry of checkpointDisplayNamesRaw || []) {
      map[entry.checkpointId] = entry.displayName;
    }
    return map;
  }, [checkpointDisplayNamesRaw]);
  const checkpointScanCandidateSites = useMemo(
    () =>
      (sites || [])
        .map((site: any) => String(site?.name || "").trim())
        .filter((name: string) => name.length > 0)
        .sort((a: string, b: string) => b.length - a.length),
    [sites]
  );
  const homeActiveTourSessions = useQuery(
    (api as any).trackTikAnalytics?.getTourSessionsList,
    activeTab === "home"
      ? {
          days: 1,
          limit: 8,
          status: "in_progress",
        }
      : ("skip" as any)
  ) as HomeActiveTourSession[] | undefined;
  useEffect(() => {
    if (!homeEventsEnabled) return;
    if (homeEventsFeedRows === undefined) return;
    const rowKey = (row: any) => String(row?.eventKey || row?._id || "");
    setHomeEventsCache((prev) =>
      mergeUniqueByKey(prev, homeEventsFeedRows, rowKey)
        .sort((a: any, b: any) => {
          const byTimestamp = (b?.timestamp || 0) - (a?.timestamp || 0);
          if (byTimestamp !== 0) return byTimestamp;
          const aPriority =
            HOME_EVENT_TIE_BREAK_PRIORITY[String(a?.eventType || "") as HomeEventType] ?? 99;
          const bPriority =
            HOME_EVENT_TIE_BREAK_PRIORITY[String(b?.eventType || "") as HomeEventType] ?? 99;
          if (aPriority !== bPriority) return aPriority - bPriority;
          return (b?.updatedAt || 0) - (a?.updatedAt || 0);
        })
        .slice(0, 300)
    );
    const newestWriteTime = maxNumericField(homeEventsFeedRows, "updatedAt");
    if (newestWriteTime > 0) {
      setHomeEventsCursorTime((prev) => Math.max(prev || 0, newestWriteTime));
    }
    if (!homeEventsHydrated) {
      setHomeEventsHydrated(true);
    }
  }, [homeEventsEnabled, homeEventsFeedRows, homeEventsHydrated]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setEventsCenterSearch(eventsCenterSearchInput.trim());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [eventsCenterSearchInput]);
  useEffect(() => {
    setEventsCenterLimit(100);
  }, [eventsCenterSearch, eventsCenterTypeFilter]);

  // Mutations
  const findOptimalVehicle = useAction(api.dispatch.findOptimalVehicle);
  const cancelDispatch = useMutation(api.dispatches.cancel);
  const startDispatch = useMutation(api.dispatches.start);
  const startRspndrDispatch = useMutation(api.dispatches.startRspndr);
  const deleteDispatchReplayData = useMutation(api.dispatches.deleteReplayData);
  const getQuickPatrolReplayVisit = useMutation((api as any).analytics.getQuickPatrolReplayVisit);
  const getExitReplayVisit = useMutation((api as any).analytics.getPatrolVisitForExitReplay);
  const updateMarkerPosition = useMutation(api.sites.updateMarkerPosition);
  const startMarkerEdit = useCallback((siteId: string) => {
    if (markerSavePendingRef.current) return;
    const site = sites?.find((s: any) => String(s._id) === siteId);
    if (!site) return;
    setMarkerEditDraft({ siteId, coordinates: siteMarkerPosition(site) });
    setMarkerEditError(null);
    setSelectedSite(null);
  }, [sites]);
  const cancelMarkerEdit = () => {
    if (markerSavePendingRef.current) return;
    setMarkerEditDraft(null);
    setMarkerEditError(null);
  };
  const saveMarkerEdit = async () => {
    if (!markerEditDraft || markerSavePendingRef.current) return;
    markerSavePendingRef.current = true;
    setMarkerEditSaving(true);
    setMarkerEditError(null);
    try {
      await updateMarkerPosition({ siteId: markerEditDraft.siteId as any,
        longitude: markerEditDraft.coordinates[0], latitude: markerEditDraft.coordinates[1] });
      setMarkerEditDraft(null);
    } catch (error) {
      setMarkerEditError(error instanceof Error ? error.message : 'Could not save marker position. Please try again.');
    } finally {
      markerSavePendingRef.current = false;
      setMarkerEditSaving(false);
    }
  };
  const updateSiteGeofence = useMutation(api.sites.updateGeofence);
  const archiveSite = useMutation(api.sites.archive);
  const unarchiveSite = useMutation(api.sites.unarchive);
  const defaultSiteMarkerSizePx = Math.max(16, Math.min(48, Math.round(mapDisplaySettings?.siteMarkerSizePx ?? 24)));
  const siteMarkerSizesByType = useMemo(
    () =>
      Object.fromEntries(
        SITE_TYPES.map((siteType) => [
          siteType,
          Math.max(
            16,
            Math.min(48, Math.round(mapDisplaySettings?.siteMarkerSizes?.[siteType] ?? defaultSiteMarkerSizePx))
          ),
        ])
      ) as Record<string, number>,
    [defaultSiteMarkerSizePx, mapDisplaySettings?.siteMarkerSizes]
  );

  useEffect(() => {
    if (!VALID_SITE_TYPE_FILTERS.has(siteTypeFilter)) {
      setSiteTypeFilter('all');
    }
  }, [siteTypeFilter]);

  // Close context menu on outside click / escape
  useEffect(() => {
    if (!siteContextMenu) return;
    const onClick = () => setSiteContextMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSiteContextMenu(null);
    };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [siteContextMenu]);

  // Close account menu on outside click / escape.
  useEffect(() => {
    if (!isAccountMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target && accountMenuRef.current?.contains(target)) return;
      setIsAccountMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsAccountMenuOpen(false);
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [isAccountMenuOpen]);

  const saveGeofenceEdit = useCallback(async () => {
    if (!geofenceEditSiteId) {
      setGeofenceSaveError("GEOFENCE-UI-002 No geofence edit session is active");
      return;
    }
    const ring = geofenceDraftRingRef.current;
    if (!ring || ring.length < 4) {
      setGeofenceSaveError("GEOFENCE-UI-003 Draft geofence is missing/invalid");
      return;
    }
    setGeofenceSaveError(null);
    try {
      await updateSiteGeofence({
        siteId: geofenceEditSiteId as any,
        coordinates: [ring],
        geoFenceType: "Polygon",
      });
      cancelGeofenceEdit();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setGeofenceSaveError(msg || "GEOFENCE-UI-004 Failed to save geofence");
    }
  }, [geofenceEditSiteId, updateSiteGeofence, cancelGeofenceEdit]);

  // Auto-focus the newest active dispatch.
  useEffect(() => {
    if (focusedDispatchId) return;
    if (!activeDispatches || activeDispatches.length === 0) return;
    setFocusedDispatchId(activeDispatches[0]._id);
  }, [focusedDispatchId, activeDispatches]);

  // NOTE: route restoration for a focused dispatch is handled by a child component
  // that only mounts once focusedDispatchId is known (avoids calling Convex queries with {} args).

  // If the selected vehicle becomes invisible/blacklisted, clear selection.
  useEffect(() => {
    if (!vehicles || !selectedVehicle) return;
    const v = vehicles.find((x: any) => x._id === selectedVehicle);
    if (!v) return;
    // Only clear if blacklisted; invisible vehicles should still be shown in the Vehicles tab.
    if (v.blacklisted) setSelectedVehicle(null);
  }, [vehicles, selectedVehicle]);

  const resolveSiteFromInput = useCallback((raw: string) => {
    if (mapSearchMode !== 'sites') return null;
    const input = raw.trim().toLowerCase();
    if (!input || !sites || sites.length === 0) return null;

    // Exact match (name)
    const exact = sites.find((s: any) => (s.name || '').trim().toLowerCase() === input);
    if (exact) return exact;

    // Starts-with match
    const starts = sites.find((s: any) => (s.name || '').trim().toLowerCase().startsWith(input));
    if (starts) return starts;

    // Contains match (name/address)
    const contains = sites.find((s: any) => {
      const name = (s.name || '').toLowerCase();
      const addr = (s.address || '').toLowerCase();
      return name.includes(input) || addr.includes(input);
    });
    if (contains) return contains;

    return null;
  }, [mapSearchMode, sites]);

  const filteredMapSites = useMemo(() => {
    if (mapSearchMode !== 'sites') return [];
    const list = sites || [];
    const q = mapSiteSearch.trim().toLowerCase();
    if (!q) return list.slice(0, 12);

    return list
      .filter((s: any) => {
        const name = (s.name || '').toLowerCase();
        const addr = (s.address || '').toLowerCase();
        return name.includes(q) || addr.includes(q);
      })
      .slice(0, 12);
  }, [sites, mapSiteSearch, mapSearchMode]);

  const tabSuggestion = useMemo(() => {
    if (mapSearchMode !== 'sites') return '';
    const q = mapSiteSearch.trim();
    if (!q) return '';
    const s = filteredMapSites[0]?.name;
    if (!s || typeof s !== 'string') return '';
    if (!s.toLowerCase().startsWith(q.toLowerCase())) return '';
    if (s.toLowerCase() === q.toLowerCase()) return '';
    return s;
  }, [filteredMapSites, mapSiteSearch, mapSearchMode]);

  useEffect(() => {
    if (mapSearchMode !== 'rspndr') {
      setRspndrSuggestions([]);
      setRspndrSearchError(null);
      rspndrSessionTokenRef.current = null;
      return;
    }

    const query = mapSiteSearch.trim();
    if (query.length < 3) {
      setRspndrSuggestions([]);
      setRspndrSearchError(null);
      return;
    }

    const requestId = ++rspndrSearchSeqRef.current;
    const t = window.setTimeout(async () => {
      try {
        if (!rspndrSessionTokenRef.current) {
          rspndrSessionTokenRef.current = createPlacesSessionToken();
        }

        const suggestions = await fetchPlacesAutocomplete(query, rspndrSessionTokenRef.current);
        if (requestId !== rspndrSearchSeqRef.current) return;
        setRspndrSearchError(null);
        setRspndrSuggestions(suggestions.slice(0, 12));
      } catch (e) {
        if (requestId !== rspndrSearchSeqRef.current) return;
        console.warn('RSPNDR autocomplete failed', e);
        const msg = e instanceof Error ? e.message : 'Failed to load address search';
        setRspndrSearchError(msg);
        setRspndrSuggestions([]);
      }
    }, 200);

    return () => window.clearTimeout(t);
  }, [mapSearchMode, mapSiteSearch]);

  const dispatchToSiteImpl = useCallback(async (site: any) => {
    if (!site) return;
    setIsDispatching(true);
    setMapDispatchError(null);
    try {
      const normalizedSite = normalizeLatLng(site.latitude, site.longitude);
      if (!normalizedSite.ok) {
        throw new Error(`[DISPATCH-DEST-002] Invalid site coordinates for "${site?.name || 'Unknown'}"`);
      }

      // Vehicles that are already assigned to an active/background dispatch should not be eligible.
      const excludeVehicleIds = (() => {
        const ids = new Set<string>();
        if (isRouteConfirmed && dispatchResult?.optimalVehicle?.vehicleId) {
          ids.add(String(dispatchResult.optimalVehicle.vehicleId));
        }
        for (const d of backgroundDispatches || []) {
          if (d?.vehicleId) ids.add(String(d.vehicleId));
        }
        return Array.from(ids);
      })();

      setDispatchOptionIndex(0);
      setIsRouteConfirmed(false);
      setDispatchStartedAt(null);
      setIsDispatchMenuOpen(false);
      setRspndrTarget(null);
      // 1) Try server-side dispatch (fastest + includes encoded polyline for Map.tsx)
      const serverResult = await findOptimalVehicle({
        siteId: site._id,
        siteName: site.name,
        siteLatitude: normalizedSite.lat,
        siteLongitude: normalizedSite.lng,
        excludeVehicleIds,
      });

      if (serverResult?.optimalVehicle?.route) {
        setDispatchResult(serverResult);
        // Highlight the site for routing, but don't open the Site Details panel.
        setMapSelectedSiteId(site._id);
        setActiveDispatchSiteId(site._id);
        if (serverResult?.optimalVehicle?.vehicleId) {
          setSelectedVehicle(serverResult.optimalVehicle.vehicleId);
        }
        return;
      }

      // 2) Fallback to browser-side routing (Routes API).
      const sitePoint = { lat: normalizedSite.lat, lng: normalizedSite.lng };
      const candidates = (vehicles || [])
        .filter((v: any) => v.isVisible && !v.blacklisted)
        .filter((v: any) => !excludeVehicleIds.includes(String(v._id)))
        .filter((v: any) => normalizeLatLng(v.latitude, v.longitude).ok)
        .map((v: any) => ({
          vehicle: v,
          approxMeters: (() => {
            const n = normalizeLatLng(v.latitude, v.longitude);
            return n.ok ? haversineMeters({ lat: n.lat, lng: n.lng }, sitePoint) : Number.POSITIVE_INFINITY;
          })(),
        }))
        .sort((a: any, b: any) => a.approxMeters - b.approxMeters)
        .slice(0, 8); // avoid hammering Directions API if fleet is large

      const options: any[] = [];
      for (const c of candidates) {
        const v = c.vehicle;
        try {
          const n = normalizeLatLng(v.latitude, v.longitude);
          if (!n.ok) continue;
          const route = await fetchRoute({ lat: n.lat, lng: n.lng }, sitePoint);
          const durationInSeconds = route.durationSeconds ?? 0;
          const eta = durationInSeconds > 0 ? new Date(Date.now() + durationInSeconds * 1000).toLocaleTimeString() : '';

          options.push({
            vehicleId: v._id,
            vehicleName: v.displayName || v.vehicleId,
            driverName: (v.driverName || "").toString().trim() || undefined,
            distance: formatDistanceFromMeters(route.distanceMeters),
            duration: durationInSeconds ? formatDurationFromSeconds(durationInSeconds) : '',
            route: { coordinates: route.coordinates },
            eta,
            _durationSeconds: durationInSeconds,
          });
        } catch (e) {
          // Skip this candidate; continue trying others.
          console.warn('Routes candidate failed', v?.displayName || v?.vehicleId, e);
        }
      }

      if (options.length === 0) {
        throw new Error('No route found (Routes API returned no results)');
      }

      options.sort((a: any, b: any) => (a._durationSeconds ?? 0) - (b._durationSeconds ?? 0));
      const fallbackResult = {
        siteName: site.name,
        optimalVehicle: options[0],
        allOptions: options,
      };

      setDispatchResult(fallbackResult);
      // Highlight the site for routing, but don't open the Site Details panel.
      setMapSelectedSiteId(site._id);
      setActiveDispatchSiteId(site._id);
      setSelectedVehicle(options[0].vehicleId);
    } catch (e: any) {
      console.error('Dispatch error:', e);
      setDispatchResult(null);
      setIsRouteConfirmed(false);
      setDispatchStartedAt(null);
      setIsDispatchMenuOpen(false);
      setActiveDispatchSiteId(null);
      setMapDispatchError(e?.message || 'Dispatch failed');
    } finally {
      setIsDispatching(false);
    }
  }, [
    findOptimalVehicle,
    vehicles,
    isRouteConfirmed,
    dispatchResult?.optimalVehicle?.vehicleId,
    backgroundDispatches,
    formatDurationFromSeconds,
  ]);

  const dispatchToRspndrImpl = useCallback(async (target: RspndrTarget) => {
    if (!target) return;
    setIsDispatching(true);
    setMapDispatchError(null);
    try {
      const normalizedTarget = normalizeLatLng(target.lat, target.lng);
      if (!normalizedTarget.ok) {
        throw new Error('[DISP-RSPNDR-002] Invalid target coordinates');
      }

      const excludeVehicleIds = (() => {
        const ids = new Set<string>();
        if (isRouteConfirmed && dispatchResult?.optimalVehicle?.vehicleId) {
          ids.add(String(dispatchResult.optimalVehicle.vehicleId));
        }
        for (const d of backgroundDispatches || []) {
          if (d?.vehicleId) ids.add(String(d.vehicleId));
        }
        return Array.from(ids);
      })();

      setDispatchOptionIndex(0);
      setIsRouteConfirmed(false);
      setDispatchStartedAt(null);
      setIsDispatchMenuOpen(false);
      setActiveDispatchSiteId(null);
      setMapSelectedSiteId(null);
      setRspndrTarget({ ...target, lat: normalizedTarget.lat, lng: normalizedTarget.lng });

      const destPoint = { lat: normalizedTarget.lat, lng: normalizedTarget.lng };
      const candidates = (vehicles || [])
        .filter((v: any) => v.isVisible && !v.blacklisted)
        .filter((v: any) => !excludeVehicleIds.includes(String(v._id)))
        .filter((v: any) => normalizeLatLng(v.latitude, v.longitude).ok)
        .map((v: any) => ({
          vehicle: v,
          approxMeters: (() => {
            const n = normalizeLatLng(v.latitude, v.longitude);
            return n.ok ? haversineMeters({ lat: n.lat, lng: n.lng }, destPoint) : Number.POSITIVE_INFINITY;
          })(),
        }))
        .sort((a: any, b: any) => a.approxMeters - b.approxMeters)
        .slice(0, 8);

      const options: any[] = [];
      for (const c of candidates) {
        const v = c.vehicle;
        try {
          const n = normalizeLatLng(v.latitude, v.longitude);
          if (!n.ok) continue;
          const route = await fetchRoute({ lat: n.lat, lng: n.lng }, destPoint);
          const durationInSeconds = route.durationSeconds ?? 0;
          const eta = durationInSeconds > 0 ? new Date(Date.now() + durationInSeconds * 1000).toLocaleTimeString() : '';

          options.push({
            vehicleId: v._id,
            vehicleName: v.displayName || v.vehicleId,
            driverName: (v.driverName || "").toString().trim() || undefined,
            distance: formatDistanceFromMeters(route.distanceMeters),
            duration: durationInSeconds ? formatDurationFromSeconds(durationInSeconds) : '',
            route: { coordinates: route.coordinates },
            eta,
            _durationSeconds: durationInSeconds,
          });
        } catch (e) {
          console.warn('RSPNDR routes candidate failed', v?.displayName || v?.vehicleId, e);
        }
      }

      if (options.length === 0) {
        throw new Error('No route found (Routes API returned no results)');
      }

      options.sort((a: any, b: any) => (a._durationSeconds ?? 0) - (b._durationSeconds ?? 0));
      const fallbackResult = {
        siteName: target.address,
        dispatchType: 'rspndr',
        targetAddress: target.address,
        targetLatitude: normalizedTarget.lat,
        targetLongitude: normalizedTarget.lng,
        optimalVehicle: options[0],
        allOptions: options,
      };

      setDispatchResult(fallbackResult);
      setSelectedVehicle(options[0].vehicleId);
    } catch (e: any) {
      console.error('RSPNDR dispatch error:', e);
      setDispatchResult(null);
      setIsRouteConfirmed(false);
      setDispatchStartedAt(null);
      setActiveDispatchSiteId(null);
      setMapSelectedSiteId(null);
      setRspndrTarget(null);
      setMapDispatchError(e?.message || 'Dispatch failed');
    } finally {
      setIsDispatching(false);
    }
  }, [
    vehicles,
    isRouteConfirmed,
    dispatchResult?.optimalVehicle?.vehicleId,
    backgroundDispatches,
    haversineMeters,
    formatDurationFromSeconds,
  ]);

  const requestDispatchToSite = useCallback(async (site: any) => {
    if (!site) return;
    // If a dispatch is actively routing, require confirmation before starting another dispatch.
    if (isRouteConfirmed && dispatchResult?.optimalVehicle) {
      const isSame = activeDispatchSiteId && site._id === activeDispatchSiteId;
      if (!isSame) {
        setPendingDispatchSite(site);
        setPendingRspndrTarget(null);
        setShowDispatchSwitchModal(true);
        return;
      }
    }
    await dispatchToSiteImpl(site);
  }, [isRouteConfirmed, dispatchResult?.optimalVehicle, activeDispatchSiteId, dispatchToSiteImpl]);

  const requestDispatchToRspndr = useCallback(async (target: RspndrTarget) => {
    if (!target) return;
    if (isRouteConfirmed && dispatchResult?.optimalVehicle) {
      const sameTarget =
        rspndrTarget &&
        Math.abs(rspndrTarget.lat - target.lat) < 1e-6 &&
        Math.abs(rspndrTarget.lng - target.lng) < 1e-6;
      if (!sameTarget) {
        setPendingDispatchSite(null);
        setPendingRspndrTarget(target);
        setShowDispatchSwitchModal(true);
        return;
      }
    }
    await dispatchToRspndrImpl(target);
  }, [isRouteConfirmed, dispatchResult?.optimalVehicle, rspndrTarget, dispatchToRspndrImpl]);

  const selectRspndrPrediction = useCallback(async (prediction: PlacesSuggestion) => {
    if (!prediction?.placeId) return;
    setMapDispatchError(null);
    try {
      const detail = await fetchPlaceDetails(prediction.placeId, rspndrSessionTokenRef.current);
      const address = detail.address || prediction.description || 'Selected address';
      const lat = detail.lat;
      const lng = detail.lng;

      rspndrSessionTokenRef.current = null;
      setMapSiteSearch(address);
      setIsMapSiteDropdownOpen(false);
      setRspndrSuggestions([]);
      setRspndrSearchError(null);

      await requestDispatchToRspndr({ address, lat, lng, placeId: prediction.placeId });
      setMapSearchMode('sites');
    } catch (e: any) {
      console.error('RSPNDR place details failed', e);
      setMapDispatchError(e?.message || 'Failed to resolve address');
    }
  }, [requestDispatchToRspndr]);

  const backgroundCurrentDispatch = useCallback(() => {
    const routedVehicleId = dispatchResult?.optimalVehicle?.vehicleId;
    if (!dispatchResult?.optimalVehicle || !routedVehicleId) return;
    const isRspndr = dispatchResult?.dispatchType === 'rspndr' || (!!rspndrTarget && !activeDispatchSiteId);
    const siteName = isRspndr
      ? (rspndrTarget?.address as string | undefined) || dispatchResult?.siteName || '—'
      : (sites?.find((s: any) => s._id === activeDispatchSiteId)?.name as string | undefined) ||
        dispatchResult?.siteName ||
        '—';
    setBackgroundDispatches((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        createdAt: Date.now(),
        siteId: isRspndr ? null : activeDispatchSiteId,
        siteName,
        vehicleId: routedVehicleId,
        vehicleName: dispatchResult.optimalVehicle.vehicleName,
        driverName: dispatchResult.optimalVehicle.driverName,
        dispatchType: isRspndr ? 'rspndr' : undefined,
        rspndrTarget: isRspndr ? rspndrTarget : null,
        dispatchResult,
        dispatchOptionIndex,
        dispatchStartedAt,
      },
    ]);

    // Clear active UI route (the background dispatch continues conceptually).
    setIsDispatchOverlaySuppressed(true);
    setIsRouteConfirmed(false);
    setDispatchStartedAt(null);
    setIsDispatchMenuOpen(false);
    setDispatchResult(null);
    setMapSelectedSiteId(null);
    setActiveDispatchSiteId(null);
    setRspndrTarget(null);
    setDispatchOptionIndex(0);
  }, [dispatchResult, activeDispatchSiteId, sites, dispatchOptionIndex, dispatchStartedAt, rspndrTarget]);

  const serializeActiveDispatch = useCallback(() => {
    const routedVehicleId = dispatchResult?.optimalVehicle?.vehicleId;
    if (!dispatchResult?.optimalVehicle || !routedVehicleId) return null;
    const isRspndr = dispatchResult?.dispatchType === 'rspndr' || (!!rspndrTarget && !activeDispatchSiteId);
    const siteName = isRspndr
      ? (rspndrTarget?.address as string | undefined) || dispatchResult?.siteName || '—'
      : (sites?.find((s: any) => s._id === activeDispatchSiteId)?.name as string | undefined) ||
        dispatchResult?.siteName ||
        '—';
    return {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      createdAt: Date.now(),
      siteId: isRspndr ? null : activeDispatchSiteId,
      siteName,
      vehicleId: routedVehicleId,
      vehicleName: dispatchResult.optimalVehicle.vehicleName,
      driverName: dispatchResult.optimalVehicle.driverName,
      dispatchType: isRspndr ? 'rspndr' : undefined,
      rspndrTarget: isRspndr ? rspndrTarget : null,
      dispatchResult,
      dispatchOptionIndex,
      dispatchStartedAt,
    };
  }, [dispatchResult, activeDispatchSiteId, sites, dispatchOptionIndex, dispatchStartedAt, rspndrTarget]);

  const switchToBackgroundDispatch = useCallback((dispatchId: string) => {
    const target = backgroundDispatches.find((d) => d.id === dispatchId);
    if (!target) return;

    // Remove target from background list and (optionally) push current active into background.
    setBackgroundDispatches((prev) => {
      const withoutTarget = prev.filter((d) => d.id !== dispatchId);
      const activeSerialized =
        isRouteConfirmed && dispatchResult?.optimalVehicle && selectedVehicle
          ? serializeActiveDispatch()
          : null;
      return activeSerialized ? [...withoutTarget, activeSerialized] : withoutTarget;
    });

    // Activate target as the current dispatch UI.
    setIsDispatchOverlaySuppressed(false);
    setIsSidebarCollapsed(true);
    setDispatchResult(target.dispatchResult);
    if (target.dispatchType === 'rspndr' || target.rspndrTarget) {
      setMapSelectedSiteId(null);
      setActiveDispatchSiteId(null);
      setRspndrTarget(target.rspndrTarget || null);
    } else {
      setMapSelectedSiteId(target.siteId);
      setActiveDispatchSiteId(target.siteId);
      setRspndrTarget(null);
    }
    setSelectedVehicle(target.vehicleId);
    setDispatchOptionIndex(typeof target.dispatchOptionIndex === 'number' ? target.dispatchOptionIndex : 0);
    setIsRouteConfirmed(true);
    setDispatchStartedAt(typeof target.dispatchStartedAt === 'number' ? target.dispatchStartedAt : Date.now());
    setIsDispatchMenuOpen(false);
  }, [backgroundDispatches, isRouteConfirmed, dispatchResult, mapSelectedSiteId, selectedVehicle, serializeActiveDispatch]);

  const removeBackgroundDispatch = useCallback((dispatchId: string) => {
    setBackgroundDispatches((prev) => prev.filter((d) => d.id !== dispatchId));
  }, []);

  const recalculateRouteForVehicleToSelectedSite = useCallback(async (vehicleId: string, siteIdOverride?: string) => {
    if (!vehicleId) return null;
    const siteId = siteIdOverride || mapSelectedSiteId;
    if (!sites || !vehicles) return null;

    let destinationPoint: { lat: number; lng: number } | null = null;
    if (siteId) {
      const site = sites.find((s: any) => s._id === siteId);
      if (!site) return null;
      const nSite = normalizeLatLng(site.latitude, site.longitude);
      if (!nSite.ok) {
        console.warn('[ROUTE-DEST-002] Invalid destination coordinates', site?.name);
        return null;
      }
      destinationPoint = { lat: nSite.lat, lng: nSite.lng };
    } else if (rspndrTarget) {
      const nTarget = normalizeLatLng(rspndrTarget.lat, rspndrTarget.lng);
      if (!nTarget.ok) {
        console.warn('[ROUTE-DEST-002] Invalid RSPNDR destination coordinates');
        return null;
      }
      destinationPoint = { lat: nTarget.lat, lng: nTarget.lng };
    }
    if (!destinationPoint) return null;

    const v = vehicles.find((x: any) => x._id === vehicleId);
    if (!v) return null;

    const nVeh = normalizeLatLng(v.latitude, v.longitude);
    if (!nVeh.ok) {
      console.warn('[ROUTE-ORIG-002] Invalid origin coordinates', v?.displayName || v?.vehicleId);
      return null;
    }

    const originPoint = { lat: nVeh.lat, lng: nVeh.lng };
    const route = await fetchRoute(originPoint, destinationPoint);
    const durationSeconds = route.durationSeconds ?? 0;
    const durationText = durationSeconds ? formatDurationFromSeconds(durationSeconds) : '';
    const distanceText = formatDistanceFromMeters(route.distanceMeters);
    const eta = durationSeconds > 0 ? new Date(Date.now() + durationSeconds * 1000).toLocaleTimeString() : undefined;

    return {
      vehicleId: v._id,
      vehicleName: v.displayName || v.vehicleId,
      driverName: (v.driverName || "").toString().trim() || undefined,
      distance: distanceText,
      duration: durationText,
      route: { coordinates: route.coordinates },
      eta: eta || '',
      _durationSeconds: durationSeconds,
    };
  }, [mapSelectedSiteId, sites, vehicles, rspndrTarget, formatDurationFromSeconds]);

  const getUpdatedEtaForOption = useCallback((opt: any) => {
    const seconds =
      opt?._durationSeconds ??
      opt?.route?.legs?.[0]?.duration?.value ??
      opt?.route?.legs?.[0]?.duration?.value; // defensive
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
      return opt?.eta;
    }
    return new Date(Date.now() + seconds * 1000).toLocaleTimeString();
  }, []);

  const currentDispatchOptions = dispatchResult?.allOptions || (dispatchResult?.optimalVehicle ? [dispatchResult.optimalVehicle] : []);
  const canCycleDispatchOptions = currentDispatchOptions.length > 1;
  const safeDispatchIndex = Math.min(Math.max(dispatchOptionIndex, 0), Math.max(currentDispatchOptions.length - 1, 0));
  const currentDispatchOption = currentDispatchOptions[safeDispatchIndex];
  const isRspndrDispatch = dispatchResult?.dispatchType === 'rspndr' || (!!rspndrTarget && !activeDispatchSiteId);

  const destinationSite = useMemo(() => {
    if (!sites || sites.length === 0) return null;
    // While routing, destination must remain locked to the active dispatch site,
    // regardless of what site the user clicks on the map.
    if (isRouteConfirmed && activeDispatchSiteId) {
      return sites.find((s: any) => s._id === activeDispatchSiteId) || null;
    }
    if (mapSelectedSiteId) return sites.find((s: any) => s._id === mapSelectedSiteId) || null;
    const name = dispatchResult?.siteName;
    if (name) {
      return sites.find((s: any) => s.name === name) || null;
    }
    return null;
  }, [sites, isRouteConfirmed, activeDispatchSiteId, mapSelectedSiteId, dispatchResult?.siteName]);

  const destinationTitle = isRspndrDispatch
    ? 'RSPNDR Dispatch'
    : destinationSite?.name || dispatchResult?.siteName || '—';
  const destinationSubtitle = isRspndrDispatch
    ? (rspndrTarget?.address || dispatchResult?.siteName || '')
    : destinationSite?.address
      ? String(destinationSite.address).replace(/\s*\d{5}(-\d{4})?$/, '')
      : '';

  // NOTE: destination geofence parsing moved to backend dispatch tick.

  // NOTE: Background dispatch cards are purely a UI affordance ("move this view to the hamburger menu").
  // Dispatch lifecycle is tracked on the backend; we don't auto-remove background entries client-side.

  // Travel clock tick (only while routing)
  useEffect(() => {
    if (!isRouteConfirmed || !dispatchStartedAt) return;
    setTravelNowMs(Date.now());
    const t = window.setInterval(() => setTravelNowMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [isRouteConfirmed, dispatchStartedAt]);

  // Keep the dispatch route polyline trimmed as Samsara updates come in (~every 3s).
  // We avoid drawing straight off-road segments here; true rerouting happens via Routes API calls.
  useEffect(() => {
    if (!isRouteConfirmed) return;
    if (isDispatchOverlaySuppressed) return;
    if (!dispatchResult?.optimalVehicle?.route?.coordinates) return;
    if (!vehicles) return;

    const routedVehicleId = dispatchResult?.optimalVehicle?.vehicleId;
    if (!routedVehicleId) return;

    const v = vehicles.find((x: any) => x._id === routedVehicleId);
    if (!v || typeof v.latitude !== "number" || typeof v.longitude !== "number") return;
    const n = normalizeLatLng(v.latitude, v.longitude);
    if (!n.ok) return;
    const current: [number, number] = [n.lng, n.lat];

    const base = (dispatchResult.optimalVehicle.route.coordinates || []) as [number, number][];
    if (!Array.isArray(base) || base.length < 2) return;

    // Find closest index on the route to current GPS, then trim.
    let bestIdx = 0;
    let bestD = Number.POSITIVE_INFINITY;
    for (let i = 0; i < base.length; i++) {
      const c = base[i];
      if (!Array.isArray(c) || typeof c[0] !== "number" || typeof c[1] !== "number") continue;
      const d = haversineMiles(current, c as [number, number]);
      if (d < bestD) {
        bestD = d;
        bestIdx = i;
      }
    }

    const MAX_TRIM_MILES = 0.1; // ~160m; don't mutate the line if we're far off-route
    if (bestD > MAX_TRIM_MILES) return;

    // Trim the traveled portion but avoid stitching a straight line to the GPS point.
    // The route line should stay on-road; reroutes come from fresh Routes API calls.
    const trimmed = base.slice(bestIdx) as [number, number][];
    if (trimmed.length < 2) return;

    // Update ETA/time/distance based on remaining polyline length (no extra Directions calls).
    const totalMiles = polylineMiles(base);
    const remainingMiles = polylineMiles(trimmed);
    const totalSeconds =
      typeof dispatchResult?.optimalVehicle?._durationSeconds === "number"
        ? dispatchResult.optimalVehicle._durationSeconds
        : undefined;
    const remainingSeconds =
      typeof totalSeconds === "number" && totalSeconds > 0 && totalMiles > 0
        ? Math.max(0, Math.round(totalSeconds * (remainingMiles / totalMiles)))
        : undefined;

    const eta =
      typeof remainingSeconds === "number"
        ? new Date(Date.now() + remainingSeconds * 1000).toLocaleTimeString()
        : dispatchResult?.optimalVehicle?.eta;

    setDispatchResult((prev: any) => {
      if (!prev?.optimalVehicle?.route?.coordinates) return prev;
      // If user is cycling options (non-DB dispatch), keep that behavior.
      return {
        ...prev,
        optimalVehicle: {
          ...prev.optimalVehicle,
          eta: eta || prev.optimalVehicle.eta,
          duration: typeof remainingSeconds === "number" ? formatDurationFromSeconds(remainingSeconds) : prev.optimalVehicle.duration,
          distance: Number.isFinite(remainingMiles) ? `${remainingMiles.toFixed(1)} mi` : prev.optimalVehicle.distance,
          route: { ...prev.optimalVehicle.route, coordinates: trimmed },
        },
      };
    });
  }, [
    vehicles,
    isRouteConfirmed,
    isDispatchOverlaySuppressed,
    dispatchResult?.optimalVehicle?.vehicleId,
    dispatchResult?.optimalVehicle?.route?.coordinates,
    haversineMiles,
    polylineMiles,
    formatDurationFromSeconds,
  ]);

  // Close dispatch menu when clicking outside
  useEffect(() => {
    if (!isDispatchMenuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const root = dispatchMenuRef.current;
      if (!root) return;
      const target = e.target as Node | null;
      if (target && root.contains(target)) return;
      setIsDispatchMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('touchstart', onDown, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('touchstart', onDown, true);
    };
  }, [isDispatchMenuOpen]);

  // NOTE: Dispatch stage transitions are handled on the backend (Convex tick).
  // We do not end/advance dispatch UI client-side based on geofence checks to avoid flicker at boundaries.

  const selectDispatchOptionIndex = useCallback(async (nextIndex: number) => {
    if (!dispatchResult) return;
    const options = dispatchResult.allOptions || (dispatchResult.optimalVehicle ? [dispatchResult.optimalVehicle] : []);
    if (!options.length) return;
    const idx = ((nextIndex % options.length) + options.length) % options.length;
    const chosen = options[idx];
    const eta = getUpdatedEtaForOption(chosen);
    const normalized = eta ? { ...chosen, eta } : chosen;

    setDispatchOptionIndex(idx);
    setDispatchResult((prev: any) => (prev ? { ...prev, optimalVehicle: normalized } : prev));
    if (normalized?.vehicleId) setSelectedVehicle(normalized.vehicleId);
    if (!normalized?.vehicleId) return;

    // Recalculate route/ETA for this vehicle (fresh Directions call).
    try {
      const requestId = ++recalcRequestIdRef.current;
      setIsRecalculatingRoute(true);
      setRecalculatingLabel('Updating route…');

      const updated = await recalculateRouteForVehicleToSelectedSite(normalized.vehicleId, activeDispatchSiteId || mapSelectedSiteId || undefined);
      if (requestId !== recalcRequestIdRef.current) return;
      if (!updated) return;

      setDispatchResult((prev: any) => {
        if (!prev) return prev;
        const prevOptions = prev.allOptions || (prev.optimalVehicle ? [prev.optimalVehicle] : []);
        const nextOptions = Array.isArray(prevOptions) ? [...prevOptions] : [];
        if (nextOptions[idx]) {
          const existing = nextOptions[idx];
          nextOptions[idx] = {
            ...existing,
            ...updated,
            driverName: existing?.driverName ?? updated?.driverName,
          };
        }
        return {
          ...prev,
          allOptions: nextOptions,
          optimalVehicle: {
            ...(prev.optimalVehicle || {}),
            ...updated,
            driverName: prev?.optimalVehicle?.driverName ?? updated?.driverName,
          },
        };
      });
    } catch (e) {
      console.warn('Recalculate route failed', e);
    } finally {
      setIsRecalculatingRoute(false);
      setRecalculatingLabel(null);
    }
  }, [dispatchResult, getUpdatedEtaForOption, recalculateRouteForVehicleToSelectedSite]);

  // Auto-update the route as the vehicle moves, so the polyline stays realistic (turnarounds, etc.).
  useEffect(() => {
    if (!isRouteConfirmed) return;
    if (!dispatchResult?.optimalVehicle) return;
    if (!mapSelectedSiteId && !activeDispatchSiteId && !rspndrTarget) return;
    if (!vehicles) return;
    if (focusedDispatchId) return;
    if (isDispatchOverlaySuppressed) return;

    const routedVehicleId = dispatchResult?.optimalVehicle?.vehicleId;
    if (!routedVehicleId) return;
    const v = vehicles.find((x: any) => x._id === routedVehicleId);
    if (!v || typeof v.latitude !== 'number' || typeof v.longitude !== 'number') return;

    const nowOrigin = { lat: v.latitude, lng: v.longitude };
    const lastOrigin = lastAutoRouteOriginRef.current;
    const now = Date.now();
    const secondsSince = lastAutoRouteRecalcAtRef.current ? (now - lastAutoRouteRecalcAtRef.current) / 1000 : Number.POSITIVE_INFINITY;

    // Initialize tracking without forcing an extra Directions call right after dispatch.
    if (!lastOrigin) {
      lastAutoRouteOriginRef.current = nowOrigin;
      lastAutoRouteRecalcAtRef.current = now;
      return;
    }

    // Recalculate on a strict cadence to keep the route aligned with live GPS.
    if (secondsSince < 3) return;
    if (isRecalculatingRoute) return;

    const requestId = ++recalcRequestIdRef.current;
    setIsRecalculatingRoute(true);
    setRecalculatingLabel('Updating route…');

    (async () => {
      try {
        const targetSiteId = activeDispatchSiteId || mapSelectedSiteId || undefined;
        const updated = await recalculateRouteForVehicleToSelectedSite(routedVehicleId, targetSiteId);
        if (requestId !== recalcRequestIdRef.current) return;
        if (!updated) return;

        lastAutoRouteOriginRef.current = nowOrigin;
        lastAutoRouteRecalcAtRef.current = Date.now();

        setDispatchResult((prev: any) => {
          if (!prev) return prev;
          const prevOptions = prev.allOptions || (prev.optimalVehicle ? [prev.optimalVehicle] : []);
          const nextOptions = Array.isArray(prevOptions) ? [...prevOptions] : [];
          // Update the currently selected option index in-place.
          if (nextOptions[safeDispatchIndex]) nextOptions[safeDispatchIndex] = { ...nextOptions[safeDispatchIndex], ...updated };
          return { ...prev, allOptions: nextOptions, optimalVehicle: updated };
        });
      } catch (e) {
        console.warn('Auto route recalc failed', e);
      } finally {
        if (requestId === recalcRequestIdRef.current) {
          setIsRecalculatingRoute(false);
          setRecalculatingLabel(null);
        }
      }
    })();
  }, [
    isRouteConfirmed,
    dispatchResult,
    mapSelectedSiteId,
    activeDispatchSiteId,
    rspndrTarget,
    vehicles,
    isRecalculatingRoute,
    recalculateRouteForVehicleToSelectedSite,
    safeDispatchIndex,
  ]);

  useEffect(() => {
    if (Array.isArray(patrolVisits)) {
      lastPatrolVisitsRef.current = patrolVisits;
    }
  }, [patrolVisits]);

  // Keep patrol list scroll anchored when pagination fetches additional rows.
  useEffect(() => {
    if (patrolScrollRestoreTopRef.current === null) return;
    if (!Array.isArray(patrolVisits)) return;
    const restoreTop = patrolScrollRestoreTopRef.current;
    patrolScrollRestoreTopRef.current = null;
    requestAnimationFrame(() => {
      if (contentBodyRef.current) contentBodyRef.current.scrollTop = restoreTop;
    });
  }, [patrolVisits]);

  const handleTabSelect = (tab: string) => {
    setIsAccountMenuOpen(false);
    // If the sidebar is collapsed, clicking any tab implies the user wants it open.
    if (isSidebarCollapsed) setIsSidebarCollapsed(false);
    // Clicking Analytics tab while already inside analytics steps back one level.
    if (tab === 'analytics' && activeTab === 'analytics' && !isSidebarCollapsed) {
      setAnalyticsSidebarBackNonce((prev) => prev + 1);
      return;
    }
    // Clicking Vehicles tab acts as "back" to the list if you're in a vehicle detail view.
    if (tab === 'vehicles') {
      setSelectedVehicle(null);
    }
    // Clicking Sites tab acts as "back" to the list if you're in a site detail view.
    if (tab === 'sites') {
      setSelectedSite(null);
      setIsCreatingSite(false);
    }
    // Clicking Tours tab steps back through the navigation stack:
    // rule editor → tour detail → tours list
    if (tab === 'tours' && activeTab === 'tours') {
      const didStepBack = toursPanelRef.current?.stepBack() ?? false;
      if (!didStepBack) {
        // Already at the list root — force remount to reset search/filter state
        setActiveTab('');
        setTimeout(() => setActiveTab(tab), 0);
      }
      return;
    }
    setActiveTab(tab);
  };

  const handleVehicleSelect = useCallback((vehicleId: string) => {
    setSelectedVehicle(vehicleId);
    setSelectedSite(null);
    setActiveTab('vehicles'); // Auto-switch to vehicles tab to show details
  }, []);

  // Vehicle stats are shown in the sidebar (Vehicles tab) now.

  const handleSiteSelect = useCallback((siteId: string, opts?: { highlightOnMap?: boolean }) => {
    if (markerEditorEnabled) { startMarkerEdit(siteId); return; }
    // If the sidebar is collapsed, clicking a site implies the user wants it open.
    if (isSidebarCollapsed) setIsSidebarCollapsed(false);
    setIsCreatingSite(false);
    setSelectedSite(siteId);
    setSiteModelFocusRequest(previous => ({ siteId, sequence: (previous?.sequence ?? 0) + 1 }));
    // Clicking a site should never retarget an active dispatch destination.
    // Keep map-selected site purely as "highlight / view" while routing.
    if (!isRouteConfirmed) {
      const hl = opts?.highlightOnMap ?? true;
      setMapSelectedSiteId(hl ? siteId : null);
    }
    setSelectedVehicle(null);
    setActiveTab('sites'); // Auto-switch to sites tab to show details in-sidebar
  }, [isSidebarCollapsed, isRouteConfirmed, markerEditorEnabled, startMarkerEdit]);

  const handleDispatch = async () => {
    if (!selectedSite) return;
    
    const site = sites?.find((s: any) => s._id === selectedSite);
    if (!site) return;
    
    await requestDispatchToSite(site);
  };

  const handleCreatePatrolLog = () => {
    if (selectedVehicle && selectedSite) {
      setShowPatrolLogModal(true);
    }
  };

  const handleReplayPatrol = useCallback((visit: any) => {
    setSelectedPatrolVisit(visit);
    setShowReplayModal(true);
  }, []);

  const handleHomeQuickPatrolReplay = useCallback(async (visitId: string) => {
    const normalizedVisitId = typeof visitId === "string" ? visitId.trim() : "";
    if (!normalizedVisitId) return;
    setHomeQuickReplayLoadingVisitId(normalizedVisitId);
    try {
      const visit = await getQuickPatrolReplayVisit({ visitId: normalizedVisitId });
      if (visit) handleReplayPatrol(visit);
    } catch (error) {
      console.error("Failed to load quick patrol replay from home event:", error);
    } finally {
      setHomeQuickReplayLoadingVisitId((current) => (current === normalizedVisitId ? null : current));
    }
  }, [getQuickPatrolReplayVisit, handleReplayPatrol]);

  const resolveExitReplayCandidate = useCallback((
    visits: any[] | undefined,
    request: { vehicleId: string; exitTimestamp: number; siteId: string | null }
  ) => {
    const replayableVisits = (visits || []).filter((visit: any) => {
      if (String(visit?.vehicleId || "").trim() !== request.vehicleId) return false;
      if (visit?.status !== "completed") return false;
      return typeof visit?.exitTime === "number" && Number.isFinite(visit.exitTime) && visit.exitTime > 0;
    });

    if (replayableVisits.length === 0) return null;

    const siteMatchedVisits =
      request.siteId
        ? replayableVisits.filter((visit: any) => String(visit?.siteId || "").trim() === request.siteId)
        : [];
    const candidatePool = siteMatchedVisits.length > 0 ? siteMatchedVisits : replayableVisits;

    return candidatePool.reduce((best: any | null, visit: any) => {
      if (!best) return visit;
      const bestDelta = Math.abs((best.exitTime || 0) - request.exitTimestamp);
      const currentDelta = Math.abs((visit.exitTime || 0) - request.exitTimestamp);
      if (currentDelta < bestDelta) return visit;
      if (currentDelta === bestDelta && (visit.exitTime || 0) > (best.exitTime || 0)) return visit;
      return best;
    }, null);
  }, []);

  const handleExitEventReplay = useCallback(async (eventKey: string, vehicleId: string, exitTimestamp: number, siteId: string | null) => {
    const normalizedVehicleId = typeof vehicleId === "string" ? vehicleId.trim() : "";
    if (!normalizedVehicleId || !Number.isFinite(exitTimestamp) || exitTimestamp <= 0) return;
    const request = {
      eventKey,
      vehicleId: normalizedVehicleId,
      exitTimestamp,
      siteId: siteId ? siteId : null,
    };
    const immediate = resolveExitReplayCandidate(recentPatrolVisitsForExitReplay, request);
    if (immediate) {
      setExitReplayLoadingKey(eventKey);
      handleReplayPatrol(immediate);
      setExitReplayLoadingKey((current) => (current === eventKey ? null : current));
      return;
    }
    setExitReplayLoadingKey(eventKey);
    try {
      const visit = await getExitReplayVisit({
        vehicleId: request.vehicleId,
        exitTimestamp: request.exitTimestamp,
        ...(request.siteId ? { siteId: request.siteId } : {}),
      });
      if (visit) {
        handleReplayPatrol(visit);
      } else {
        console.warn("No replay visit found for exit event request:", request);
      }
    } catch (error) {
      console.error("Failed to load patrol replay for exit event:", error);
    } finally {
      setExitReplayLoadingKey((current) => (current === eventKey ? null : current));
    }
  }, [recentPatrolVisitsForExitReplay, resolveExitReplayCandidate, getExitReplayVisit, handleReplayPatrol]);


  // Vehicles rendered on the map: respect visibility + blacklist.
  // Keep memoized so Map doesn't reconcile markers on unrelated parent renders.
  const visibleVehiclesForMap = useMemo(() => {
    if (!vehicles) return [];
    return vehicles.filter((vehicle: any) => vehicle.isVisible && !vehicle.blacklisted);
  }, [vehicles]);

  const filteredVehicles = useMemo(
    () => selectFleetVehicles<any>(vehicles || [], vehicleSearchQuery),
    [vehicles, vehicleSearchQuery]
  );

  const homeAssignedVehiclesByGuard = useMemo(() => {
    const byGuard = new globalThis.Map<string, any[]>();
    const stableVehicles = [...(vehicles || [])]
      .filter((vehicle: any) => !vehicle.blacklisted)
      .sort((a: any, b: any) => {
        const ao = typeof a?.order === "number" ? a.order : Number.POSITIVE_INFINITY;
        const bo = typeof b?.order === "number" ? b.order : Number.POSITIVE_INFINITY;
        if (ao !== bo) return ao - bo;
        const aLabel = String(a?.displayName || a?.vehicleId || "").trim();
        const bLabel = String(b?.displayName || b?.vehicleId || "").trim();
        const byName = aLabel.localeCompare(bLabel);
        if (byName !== 0) return byName;
        return String(a?._id || "").localeCompare(String(b?._id || ""));
      });

    for (const vehicle of stableVehicles) {
      const guard = sanitizeGuardLabel(String(vehicle?.driverName || vehicle?.addressBookEntry || "").trim());
      if (!guard) continue;
      const guardKey = guard.toLowerCase();
      const list = byGuard.get(guardKey) || [];
      list.push(vehicle);
      byGuard.set(guardKey, list);
    }

    return byGuard;
  }, [vehicles]);

  const homeVehicleLabels = useMemo(() => {
    const next = new globalThis.Map<string, { assignedGuardLabel: string; vehicleLabel: string }>();
    for (const vehicle of vehicles || []) {
      const vehicleDocId = typeof vehicle?._id === "string" ? vehicle._id : "";
      const vehiclePublicId = typeof vehicle?.vehicleId === "string" ? vehicle.vehicleId.trim() : "";
      if (!vehicleDocId && !vehiclePublicId) continue;
      const rawDriverName = String(vehicle?.driverName || "").trim();
      const driverLower = rawDriverName.toLowerCase();
      const guard =
        rawDriverName &&
        driverLower !== "unknown" &&
        driverLower !== "unknown guard" &&
        driverLower !== "unknown location" &&
        !rawDriverName.includes("visit.guardName") &&
        !rawDriverName.includes("||")
          ? rawDriverName
          : "";
      const vehicleLabel =
        String(vehicle?.displayName || vehicle?.vehicleId || vehiclePublicId || vehicleDocId).trim() ||
        vehiclePublicId ||
        vehicleDocId;
      const labelRow = {
        assignedGuardLabel: guard,
        vehicleLabel,
      };
      // Alerts are keyed by `vehicleId` (public ID), but keep doc-id mapping as a fallback.
      if (vehiclePublicId) next.set(vehiclePublicId, labelRow);
      if (vehicleDocId) next.set(vehicleDocId, labelRow);
    }
    return next;
  }, [vehicles]);

  const homeEventRows = homeEventsHydrated ? homeEventsCache : (homeEventsFeedRows || []);

  const mapHomeEventRows = useCallback((rows: any[], maxRows: number): HomeEventRow[] => {
    const merged: HomeEventRow[] = [];

    for (const row of rows) {
      const typeRaw = String(row?.eventType || "").trim().toLowerCase();
      if (
        typeRaw !== "entry" &&
        typeRaw !== "checkpoint_scan" &&
        typeRaw !== "checkpoint_rule" &&
        typeRaw !== "double_hit" &&
        typeRaw !== "exit" &&
        typeRaw !== "quick_patrol"
      ) {
        continue;
      }
      const type = typeRaw as HomeEventType;
      const timestamp = typeof row?.timestamp === "number" ? row.timestamp : 0;
      if (!Number.isFinite(timestamp) || timestamp <= 0) continue;
      const siteName = String(row?.siteName || "Unknown Site").trim() || "Unknown Site";
      const vehicleId = String(row?.vehicleId || "").trim();
      const vehicleName = String(row?.vehicleName || vehicleId || "Vehicle").trim() || "Vehicle";
      const guard = sanitizeGuardLabel(String(row?.guardName || "").trim());
      const guardEmployeeId =
        typeof row?.guardEmployeeId === "number" && Number.isFinite(row.guardEmployeeId)
          ? Math.floor(row.guardEmployeeId)
          : 0;

      if (type === "entry" || type === "exit") {
        const labels = homeVehicleLabels.get(vehicleId);
        const assignedGuardLabel = labels?.assignedGuardLabel || "";
        const resolvedVehicleLabel = labels?.vehicleLabel || vehicleName;
        const actorLabel = guard || assignedGuardLabel || resolvedVehicleLabel;
        const patrolDurationSeconds =
          typeof row?.patrolDurationSeconds === "number" && Number.isFinite(row.patrolDurationSeconds)
            ? row.patrolDurationSeconds
            : 0;
        const patrolDurationMs = patrolDurationSeconds > 0 ? patrolDurationSeconds * 1000 : 0;
        const detail =
          type === "exit" && patrolDurationMs > 0
            ? `Duration: ${formatDurationCompactMs(patrolDurationMs)}`
            : "";
        merged.push({
          key: `event:${String(row?.eventKey || row?._id || `${type}:${timestamp}`)}`,
          type,
          timestamp,
          siteName,
          guardLabel: actorLabel || "Vehicle",
          detail,
          vehicleLabel: null,
          quickPatrolVisitId: null,
          exitVehicleId: type === "exit" ? (vehicleId || null) : null,
          exitSiteId: type === "exit" ? (String(row?.siteId || "").trim() || null) : null,
        });
        continue;
      }

      if (type === "checkpoint_scan") {
        const checkpointName = String(row?.checkpointName || "").trim();
        const resolvedCheckpointSiteName = resolveCheckpointScanSiteNameForDisplay({
          rawSiteName: siteName,
          rawCheckpointName: checkpointName,
          candidateSites: checkpointScanCandidateSites,
        });
        const checkpointCount =
          typeof row?.checkpointCount === "number" && Number.isFinite(row.checkpointCount) && row.checkpointCount > 0
            ? Math.floor(row.checkpointCount)
            : 0;
        const checkpointTotal =
          typeof row?.checkpointTotal === "number" && Number.isFinite(row.checkpointTotal) && row.checkpointTotal > 0
            ? Math.floor(row.checkpointTotal)
            : 0;
        merged.push({
          key: `event:${String(row?.eventKey || row?._id || `${type}:${timestamp}`)}`,
          type,
          timestamp,
          siteName: resolvedCheckpointSiteName || siteName,
          guardLabel: guard || `Guard ${guardEmployeeId || "Unknown"}`,
          detail:
            checkpointCount > 0 && checkpointTotal > 0 ? `Checkpoint: ${checkpointCount}/${checkpointTotal}` : "",
          vehicleLabel: null,
          quickPatrolVisitId: null,
          exitVehicleId: null,
          exitSiteId: null,
        });
        continue;
      }

      if (type === "checkpoint_rule") {
        const checkpointId =
          typeof row?.checkpointId === "number" && Number.isFinite(row.checkpointId) && row.checkpointId > 0
            ? Math.floor(row.checkpointId)
            : 0;
        const checkpointLabel =
          (checkpointId > 0 ? checkpointDisplayNameMap[checkpointId] : "") ||
          String(row?.checkpointName || "").trim() ||
          `Checkpoint ${checkpointId || "Unknown"}`;
        const ruleLabel =
          String(row?.checkpointRuleLabel || "")
            .trim()
            .replace(/^Checkpoint must be scanned/i, "Must be scanned") || "Checkpoint rule violation";
        const reason = String(row?.checkpointRuleReason || "").trim() || "Rule was not followed";
        merged.push({
          key: `event:${String(row?.eventKey || row?._id || `${type}:${timestamp}`)}`,
          type,
          timestamp,
          siteName,
          guardLabel: guard || `Guard ${guardEmployeeId || "Unknown"}`,
          detail: `${checkpointLabel} • ${reason}`,
          vehicleLabel: null,
          quickPatrolVisitId: null,
          exitVehicleId: null,
          exitSiteId: null,
          checkpointRuleName: checkpointLabel,
          checkpointRuleLabel: ruleLabel,
        });
        continue;
      }

      if (type === "double_hit") {
        const minutesBetween =
          typeof row?.minutesBetween === "number" && Number.isFinite(row.minutesBetween)
            ? Math.max(1, Math.floor(row.minutesBetween))
            : 1;
        merged.push({
          key: `event:${String(row?.eventKey || row?._id || `${type}:${timestamp}`)}`,
          type,
          timestamp,
          siteName,
          guardLabel: guard || `Guard ${guardEmployeeId || "Unknown"}`,
          detail: `${minutesBetween} min between hits`,
          vehicleLabel: null,
          quickPatrolVisitId: null,
          exitVehicleId: null,
          exitSiteId: null,
        });
        continue;
      }

      const durationMs =
        typeof row?.patrolDurationMs === "number" && Number.isFinite(row.patrolDurationMs)
          ? Math.max(0, Math.floor(row.patrolDurationMs))
          : 0;
      const percentFaster =
        typeof row?.percentFaster === "number" && Number.isFinite(row.percentFaster)
          ? Math.max(0, Math.round(row.percentFaster))
          : null;
      const fasterLabel = percentFaster !== null ? `${percentFaster}% Faster` : "Quick Patrol";
      const durationLabel = formatDurationCompactMs(durationMs);
      const quickPatrolVisitId = String(row?.quickPatrolVisitId || "").trim() || null;
      merged.push({
        key: `event:${String(row?.eventKey || row?._id || `${type}:${timestamp}`)}`,
        type: "quick_patrol",
        timestamp,
        siteName,
        guardLabel: guard || "Unassigned",
        detail: `${fasterLabel} • ${durationLabel}`,
        vehicleLabel: guard ? null : vehicleName,
        quickPatrolVisitId,
        exitVehicleId: null,
        exitSiteId: null,
      });
    }

    return merged
      .sort((a, b) => {
        const byTimestamp = b.timestamp - a.timestamp;
        if (byTimestamp !== 0) return byTimestamp;
        const aPriority = HOME_EVENT_TIE_BREAK_PRIORITY[a.type] ?? 99;
        const bPriority = HOME_EVENT_TIE_BREAK_PRIORITY[b.type] ?? 99;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return a.key.localeCompare(b.key);
      })
      .slice(0, maxRows);
  }, [homeVehicleLabels, checkpointDisplayNameMap, checkpointScanCandidateSites]);

  const homeEvents = useMemo<HomeEventRow[]>(
    () => mapHomeEventRows(homeEventRows, 40),
    [homeEventRows, mapHomeEventRows]
  );
  const eventsCenterEvents = useMemo<HomeEventRow[]>(
    () => mapHomeEventRows(eventsCenterRows || [], eventsCenterLimit),
    [eventsCenterRows, eventsCenterLimit, mapHomeEventRows]
  );

  const renderHomeEventCard = useCallback((event: HomeEventRow): ReactNode => {
    const isQuickPatrol = event.type === "quick_patrol" && !!event.quickPatrolVisitId;
    const isExitReplayable = event.type === "exit" && !!event.exitVehicleId;
    const isLoadingReplay =
      (isQuickPatrol && homeQuickReplayLoadingVisitId === event.quickPatrolVisitId) ||
      (isExitReplayable && exitReplayLoadingKey === event.key);
    const typeLabel =
      event.type === "entry"
        ? "Site Entry"
        : event.type === "exit"
          ? "Site Exit"
          : event.type === "double_hit"
            ? "Double Hit"
            : event.type === "checkpoint_rule"
              ? "Checkpoint Rule"
              : event.type === "checkpoint_scan"
                ? "Checkpoint Scan"
                : "Quick Patrol";
    const actionLabel =
      event.type === "entry"
        ? "entered"
        : event.type === "exit"
          ? "exited"
          : event.type === "checkpoint_scan"
            ? "scanned"
            : event.type === "checkpoint_rule"
              ? "violated"
              : event.type === "double_hit"
                ? "double hit"
                : "patrolled";
    const footerLeft =
      event.type === "exit" ||
      event.type === "double_hit" ||
      event.type === "quick_patrol" ||
      event.type === "checkpoint_rule" ||
      event.type === "checkpoint_scan"
        ? event.detail
        : "";
    const footerDetail = `${footerLeft}${isLoadingReplay ? `${footerLeft ? " • " : ""}Opening replay...` : ""}`;
    const checkpointRuleName = event.checkpointRuleName || event.siteName;
    const checkpointRuleLabel = event.checkpointRuleLabel || event.detail;
    const isCheckpointRule = event.type === "checkpoint_rule";
    const footerText = isCheckpointRule ? checkpointRuleLabel : footerDetail;
    const hasFooterDetail = footerText.trim().length > 0;
    const footerClass = hasFooterDetail
      ? "home-event-footer home-event-footer--with-detail"
      : "home-event-footer home-event-footer--compact";
    const itemDensityClass = hasFooterDetail ? "home-event-item--detail" : "home-event-item--compact";
    const content = (
      <>
        <div className="home-event-top">
          <div className="home-event-title-line">
            <span className="home-event-actor">{event.guardLabel}</span>
            <span className="home-event-action">{actionLabel}</span>
          </div>
          <span className={`home-event-badge ${event.type}`}>{typeLabel}</span>
        </div>
        <div
          className="home-event-site-line"
          title={isCheckpointRule ? checkpointRuleName : event.siteName}
        >
          {isCheckpointRule ? checkpointRuleName : event.siteName}
        </div>
        <div className={footerClass}>
          {hasFooterDetail ? (
            <span className="home-event-footer-left" title={footerText}>{footerText}</span>
          ) : null}
          <span className="home-event-time">{formatEventTimestamp(event.timestamp)}</span>
        </div>
      </>
    );

    if (isQuickPatrol && event.quickPatrolVisitId) {
      return (
        <button
          key={event.key}
          type="button"
          className={`home-event-item home-event-item--clickable ${itemDensityClass} ${event.type}`}
          onClick={() => void handleHomeQuickPatrolReplay(event.quickPatrolVisitId as string)}
          disabled={isLoadingReplay}
        >
          {content}
        </button>
      );
    }

    if (isExitReplayable) {
      return (
        <button
          key={event.key}
          type="button"
          className={`home-event-item home-event-item--clickable ${itemDensityClass} ${event.type}`}
          onClick={() => void handleExitEventReplay(event.key, event.exitVehicleId as string, event.timestamp, event.exitSiteId)}
          disabled={isLoadingReplay}
        >
          {content}
        </button>
      );
    }

    return (
      <div
        key={event.key}
        className={`home-event-item ${itemDensityClass} ${event.type}`}
      >
        {content}
      </div>
    );
  }, [homeQuickReplayLoadingVisitId, exitReplayLoadingKey, handleHomeQuickPatrolReplay, handleExitEventReplay]);

  const homeEventsDisplayLoading = !homeEventsReady || (homeEventsEnabled && !homeEventsHydrated);
  const eventsCenterDisplayLoading = activeTab === "alerts" && eventsCenterRows === undefined;
  const homeActiveToursLoading = activeTab === "home" && homeActiveTourSessions === undefined;

  const homeActiveTours = useMemo(() => {
    const list = Array.isArray(homeActiveTourSessions) ? homeActiveTourSessions : [];
    return [...list]
      .filter((session) => typeof session?.tourSessionId === "number")
      .sort((a, b) => {
        const aStart = new Date(a?.startTime as any).getTime();
        const bStart = new Date(b?.startTime as any).getTime();
        const aSafe = Number.isFinite(aStart) ? aStart : 0;
        const bSafe = Number.isFinite(bStart) ? bStart : 0;
        return bSafe - aSafe;
      })
      .slice(0, 8);
  }, [homeActiveTourSessions]);
  const homePatrolCardsLoading =
    activeTab === "home" && (vehicles === undefined || homeActiveToursLoading);
  const homePatrolCards = useMemo<HomePatrolCardRow[]>(() => {
    const toMs = (value: unknown): number => {
      const ms = new Date(value as any).getTime();
      return Number.isFinite(ms) ? ms : 0;
    };

    const cards: HomePatrolCardRow[] = [];
    for (const tour of homeActiveTours) {
      const guardLabel = sanitizeGuardLabel(String(tour?.employeeName || "").trim());
      const guardKey = guardLabel ? guardLabel.toLowerCase() : "";
      if (!guardKey) continue;
      const vehicle = (homeAssignedVehiclesByGuard.get(guardKey) || [])[0] || null;
      if (!vehicle) continue;
      const hitCount =
        typeof tour?.totalScans === "number" && Number.isFinite(tour.totalScans)
          ? Math.max(0, Math.floor(tour.totalScans))
          : Array.isArray(tour?.scans)
            ? tour.scans.length
            : 0;
      const completionPct =
        typeof tour?.completionPercentage === "number" && Number.isFinite(tour.completionPercentage)
          ? Math.max(0, Math.min(100, Math.round(tour.completionPercentage)))
          : 0;
      const startedAtMs = toMs(tour?.startTime);
      cards.push({
        key: `tour:${String(tour?.tourSessionId || startedAtMs)}:vehicle:${String(vehicle?._id || vehicle?.vehicleId || guardKey)}`,
        guardLabel,
        vehicle,
        tour,
        hitCount,
        completionPct,
        startedAtMs,
      });
    }

    return cards
      .sort((a, b) => {
        if (b.hitCount !== a.hitCount) return b.hitCount - a.hitCount;
        if (b.completionPct !== a.completionPct) return b.completionPct - a.completionPct;
        if (b.startedAtMs !== a.startedAtMs) return b.startedAtMs - a.startedAtMs;
        const guardCmp = a.guardLabel.localeCompare(b.guardLabel);
        if (guardCmp !== 0) return guardCmp;
        return a.key.localeCompare(b.key);
      })
      .slice(0, 12);
  }, [homeActiveTours, homeAssignedVehiclesByGuard]);

  const profileName = ((profile?.name as string | undefined) || sessionDisplayName || "Employee").trim() || "Employee";
  const profileEmail = ((profile?.email as string | undefined) || sessionEmail || "No email").trim() || "No email";
  const profileImageUrl = (profile?.imageUrl as string | null | undefined) || null;
  const profileInitials = profileName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase() || "")
    .join("") || (profileEmail[0]?.toUpperCase() || "U");

  const bodycams = useBodycams(activeTab === 'bodycams', sites || []);
  const announcedBodycams = useRef(new Set<string>());
  useEffect(() => {
    if (!bodycams.monitoring) return;
    const activated = newActivations(bodycams.overview?.devices || [], announcedBodycams.current, Date.now());
    const newest = activated[activated.length - 1];
    if (!newest) return;
    setActiveTab('bodycams'); setIsSidebarCollapsed(false);
    bodycams.select(`device:${newest.deviceId}`);
  }, [bodycams.overview, bodycams.select, bodycams.monitoring]);
  const selectBodycam = useCallback((id: string) => {
    setActiveTab('bodycams'); setIsSidebarCollapsed(false);
    bodycams.select(id);
  }, [bodycams.select]);

  const reportsEnabled = useQuery(api.trackTikReports.getFeatureStatus)?.enabled === true;
  const tabConfig = [
    { id: 'home', label: 'Home', icon: HomeIcon },
    { id: 'dispatches', label: 'Dispatches', icon: DispatchIcon },
    { id: 'vehicles', label: 'Vehicles', icon: VehicleIcon },
    { id: 'sites', label: 'Sites', icon: SiteIcon },
    { id: 'bodycams', label: 'Bodycams', icon: BodycamIcon },
    { id: 'patrols', label: 'Patrol Logs', icon: PatrolIcon },
    { id: 'tours', label: 'Tours', icon: ToursIcon },
    { id: 'alerts', label: 'Events Center', icon: AlertIcon },
    ...(reportsEnabled ? [
      { id: 'assignments', label: 'Assignments', icon: _AssignmentsIcon },
      { id: 'reports', label: 'Reports', icon: _ReportsIcon },
    ] : []),
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  useEffect(() => {
    if (!sessionDisplayName && !sessionEmail) return;
    void syncMyProfileIdentity({
      fallbackName: sessionDisplayName || undefined,
      fallbackEmail: sessionEmail || undefined,
    }).catch((error) => {
      console.warn("Profile identity sync skipped", error);
    });
  }, [sessionDisplayName, sessionEmail, syncMyProfileIdentity]);

  const handleAnalyticsHeaderStateChange = useCallback((state: AnalyticsHeaderState) => {
    const title = (state?.title || 'Analytics').trim() || 'Analytics';
    setAnalyticsHeaderTitle(title);
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics') return;
    setAnalyticsHeaderTitle('Analytics');
  }, [activeTab]);

  const effectiveShowGeofences =
    geofenceEditorEnabled || (showGeofences && geofenceWarmReady);
  const isReportsMode = reportsEnabled && activeTab === 'reports';
  const isAssignmentsMode = reportsEnabled && activeTab === 'assignments';
  const isFullscreenMode = isReportsMode || isAssignmentsMode;

  return (
    <NotificationSystem>
      <SpeedInsights />
      <div className={`app ${isSidebarCollapsed || isFullscreenMode ? 'sidebar-collapsed' : ''} ${isFullscreenMode ? 'fullscreen-mode' : ''} ${isReportsMode ? 'reports-mode' : ''} ${isAssignmentsMode ? 'assignments-mode' : ''}`}>
        {focusedDispatchId ? (
          <FocusedDispatchRouteSync
            focusedDispatchId={focusedDispatchId}
            sites={sites as any}
            dispatchResult={dispatchResult}
            isRouteConfirmed={isRouteConfirmed}
            isSuppressed={isDispatchOverlaySuppressed}
            setDispatchResult={setDispatchResult}
            setIsRouteConfirmed={setIsRouteConfirmed}
            setDispatchStartedAt={setDispatchStartedAt}
            setActiveDispatchSiteId={setActiveDispatchSiteId}
            setMapSelectedSiteId={setMapSelectedSiteId}
            setRspndrTarget={setRspndrTarget}
          />
        ) : null}
        {/* Primary Sidebar - Icon Bar (Always Condensed) */}
        <nav className="primary-sidebar collapsed" aria-label="Primary navigation">
          <div className="icon-bar">
            <div className="icon-tabs-main">
              {tabConfig.slice(0, -1).map((tab) => (
                <button
                  key={tab.id}
                  className={`icon-tab ${!isSidebarCollapsed && activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabSelect(tab.id)}
                  title={tab.label}
                  aria-label={tab.label}
                  aria-controls="sidebar-content"
                  aria-expanded={!isSidebarCollapsed && activeTab === tab.id}
                  aria-current={!isSidebarCollapsed && activeTab === tab.id ? 'page' : undefined}
                >
                  <tab.icon />
                </button>
              ))}
            </div>
            <div className="icon-tabs-bottom" ref={accountMenuRef}>
              <button
                type="button"
                className={`icon-tab icon-tab-avatar ${isAccountMenuOpen ? 'active' : ''}`}
                onClick={() => setIsAccountMenuOpen((open) => !open)}
                title="Account"
                aria-label="Account"
                aria-haspopup="menu"
                aria-expanded={isAccountMenuOpen}
              >
                {profileImageUrl ? (
                  <img src={profileImageUrl} alt="Profile" className="icon-tab-avatar-img" />
                ) : (
                  <span className="icon-tab-avatar-fallback">{profileInitials}</span>
                )}
              </button>
              {isAccountMenuOpen ? (
                <div className="sidebar-account-menu" role="menu" aria-label="Account actions">
                  <button
                    type="button"
                    className="sidebar-account-head sidebar-account-head-btn"
                    role="menuitem"
                    onClick={() => handleTabSelect('profile')}
                  >
                    <div className="sidebar-account-avatar-wrap" aria-hidden="true">
                      {profileImageUrl ? (
                        <img src={profileImageUrl} alt="" className="sidebar-account-avatar" />
                      ) : (
                        <span className="sidebar-account-avatar-fallback">{profileInitials}</span>
                      )}
                    </div>
                    <div className="sidebar-account-meta">
                      <div className="sidebar-account-name">{profileName}</div>
                      <div className="sidebar-account-email">{profileEmail}</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="sidebar-account-action"
                    role="menuitem"
                    onClick={() => handleTabSelect('settings')}
                  >
                    Settings
                  </button>
                  <button
                    type="button"
                    className="sidebar-account-action signout"
                    role="menuitem"
                    onClick={() => {
                      setIsAccountMenuOpen(false);
                      void onSignOut();
                    }}
                  >
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </nav>

        {/* Secondary Sidebar - Content Panel */}
        {!isFullscreenMode ? (
        <div className="sidebar-slide-viewport">
        <div
          id="sidebar-content"
          className={`secondary-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}
          aria-hidden={isSidebarCollapsed}
          {...(isSidebarCollapsed ? { inert: '' } : {})}
        >
          <button
            type="button"
            className="sidebar-collapse-handle"
            onClick={() => {
              setIsSidebarCollapsed(true);
              document.querySelector<HTMLButtonElement>('.primary-sidebar .icon-tab.active')?.focus();
            }}
            aria-controls="sidebar-content"
            aria-expanded={!isSidebarCollapsed}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 6l-6 6 6 6" />
            </svg>
          </button>
          <div className="content-header">
            <h2 className="content-title">
              {activeTab === 'analytics'
                ? analyticsHeaderTitle
                : activeTab === 'profile'
                  ? 'Profile'
                  : tabConfig.find(tab => tab.id === activeTab)?.label}
            </h2>
          </div>

          <div className={`content-body ${activeTab === 'home' ? 'content-body--home' : ''}`} ref={contentBodyRef}>
            {activeTab === 'bodycams' && <BodycamPanel controller={bodycams} />}
            {activeTab === 'home' && (
              <SidebarPanel>
                <div className="home-panel home-dashboard">
                <section className="home-patrol-cards" aria-label="Active Patrols">
                  <div className="home-patrol-cards-header">
                    <h3 className="home-patrol-cards-title">Active patrols <span className="home-section-count">{homePatrolCardsLoading ? '—' : homePatrolCards.length}</span></h3>
                    <button type="button" className="home-section-link" onClick={() => handleTabSelect('tours')}>View tours <span aria-hidden="true">→</span></button>
                  </div>
                  {homePatrolCardsLoading ? (
                    <div className="home-events-empty">Loading active patrols…</div>
                  ) : homePatrolCards.length === 0 ? (
                    <div className="home-events-empty">No active patrols right now.</div>
                  ) : (
                    <div className="home-patrol-cards-list">
                      {homePatrolCards.map((card) => {
                        const vehicle = card.vehicle;
                        const tour = card.tour;
                        const vehicleName = vehicle
                          ? String(vehicle.displayName || vehicle.vehicleId || "Vehicle").trim() || "Vehicle"
                          : "Vehicle";
                        const address = vehicle ? String(vehicle.samsaraAddress || "").trim() : "";
                        const startAt = new Date(tour?.startTime as any);
                        const startMs = startAt.getTime();
                        const hasStart = Number.isFinite(startMs) && startMs > 0;
                        const durationMinutes =
                          typeof tour?.duration === "number" && Number.isFinite(tour.duration)
                            ? Math.max(0, Math.round(tour.duration))
                            : 0;
                        const completionRaw =
                          typeof tour?.completionPercentage === "number" && Number.isFinite(tour.completionPercentage)
                            ? tour.completionPercentage
                            : 0;
                        const completion = Math.max(0, Math.min(100, Math.round(completionRaw)));
                        const totalScans =
                          typeof tour?.totalScans === "number" && Number.isFinite(tour.totalScans)
                            ? tour.totalScans
                            : Array.isArray(tour?.scans)
                              ? tour.scans.length
                              : 0;
                        const totalStops =
                          typeof tour?.totalCheckpointsInTour === "number" &&
                          Number.isFinite(tour.totalCheckpointsInTour) &&
                          tour.totalCheckpointsInTour > 0
                            ? tour.totalCheckpointsInTour
                            : totalScans;
                        const routeBadge = tour?.tourName || "Active Tour";
                        const timeLabel = hasStart
                          ? startAt.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })
                          : "--:--";
                        const progressMeta = `${timeLabel} · ${durationMinutes}min · ${totalScans}/${totalStops} stops`;
                        const progressLabel = `${completion}%`;
                        const fillWidth = completion;
                        return (
                          <button
                            key={card.key}
                            type="button"
                            className="home-patrol-card"
                            onClick={() => {
                              setActiveTab("tours");
                            }}
                          >
                            <div className="home-patrol-top">
                              <span className="home-patrol-avatar" aria-hidden="true">{card.guardLabel.split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('')}</span>
                              <div className="home-patrol-top-left">
                                <div className="home-patrol-officer">{card.guardLabel}</div>
                                <div className="home-patrol-vehicle">{vehicleName}</div>
                              </div>
                              <span className="home-patrol-badge">{routeBadge}</span>
                            </div>
                            {address ? (
                              <div className="home-patrol-address">{address}</div>
                            ) : (
                              <div className="home-patrol-address home-patrol-address--empty">No live address</div>
                            )}
                            <div className="home-patrol-rule" />
                            <div className="home-patrol-progress-row">
                              <span className="home-patrol-pct">{progressLabel}</span>
                              <span className="home-patrol-progress-meta">{progressMeta}</span>
                            </div>
                            <div className="home-patrol-track" role="progressbar" aria-label={`${card.guardLabel} tour progress`} aria-valuenow={completion} aria-valuemin={0} aria-valuemax={100}>
                              <div
                                className="home-patrol-track-fill"
                                style={{ width: `${fillWidth}%` }}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>
                <section
                  className="home-events-center"
                  aria-label="Events Center"
                >
                  <div className="home-events-header">
                    <h3 className="home-events-title">Recent activity</h3>
                    <button type="button" className="home-section-link" onClick={() => handleTabSelect('alerts')}>All events <span aria-hidden="true">→</span></button>
                  </div>
                  {homeEventsDisplayLoading ? (
                    <div className="home-events-empty">Loading recent events…</div>
                  ) : homeEvents.length === 0 ? (
                    <div className="home-events-empty">No recent events found.</div>
                  ) : (
                    <div className="home-events-list home-events-list--home">
                      {homeEvents.map(renderHomeEventCard)}
                    </div>
                  )}
                </section>

                {selectedVehicle && selectedSite && (
                  <div style={{ marginTop: '20px' }}>
                    <button className="btn btn-primary" onClick={handleDispatch}>
                      Dispatch Vehicle
                    </button>
                    <button className="btn" onClick={handleCreatePatrolLog} style={{ marginLeft: '10px' }}>
                      Create Patrol Log
                    </button>
                  </div>
                )}
                </div>
              </SidebarPanel>
            )}

            {activeTab === 'profile' && (
              <SidebarPanel>
                <EmployeeProfilePanel
                  onBack={() => setActiveTab('home')}
                  fallbackName={sessionDisplayName}
                  fallbackEmail={sessionEmail}
                />
              </SidebarPanel>
            )}

            {activeTab === 'vehicles' && (
              <SidebarPanel>
                <div className="vehicles-panel">
                {vehicles ? (
                  selectedVehicle ? (
                    (() => {
                      const v = vehicles.find((x: any) => x._id === selectedVehicle);
                      if (!v || v.blacklisted) return <div className="empty-state">Vehicle not found</div>;
                      return (
                        <VehicleDetailsSidebar
                          vehicle={v}
                          onBack={() => setSelectedVehicle(null)}
                          heroImageUrl={appAssets?.patrol_vehicle || null}
                        />
                      );
                    })()
                  ) : (
                    <VehicleFleetList vehicles={filteredVehicles} search={vehicleSearchQuery}
                      onSearch={setVehicleSearchQuery} onSelect={handleVehicleSelect} />
                  )
                ) : (
                  <div className="loading">
                    <div className="spinner"></div>
                    Loading vehicles...
                  </div>
                )}
                </div>
              </SidebarPanel>
            )}

            {activeTab === 'sites' && (
              <SidebarPanel>
                <div
                  className={`sites-panel ${
                    (isCreatingSite || (selectedSite && (sites || archivedSites))) && !(geofenceEditorEnabled && geofenceEditSiteId)
                      ? "sites-panel-details"
                      : ""
                  }`}
                >
                {isCreatingSite && !(geofenceEditorEnabled && geofenceEditSiteId) ? (
                  <SiteDetailsPanel
                    site={{
                      _id: "__new__",
                      name: "",
                      address: "",
                      latitude: 0,
                      longitude: 0,
                      imageUrl: null,
                      imageThumbUrl: null,
                      description: "",
                      borderingRoads: "",
                      propertyManagerName: "",
                      propertyManagerPhone: "",
                      propertyManagerEmail: "",
                      businesses: [],
                    }}
                    isNew
                    onCreated={(newSiteId) => {
                      setSelectedSite(newSiteId);
                      setMapSelectedSiteId(newSiteId);
                      setIsCreatingSite(false);
                      if (isSidebarCollapsed) setIsSidebarCollapsed(false);
                      setActiveTab("sites");
                    }}
                    onClose={() => {
                      setIsCreatingSite(false);
                    }}
                  />
                ) : selectedSite && (sites || archivedSites) && !(geofenceEditorEnabled && geofenceEditSiteId) ? (
                  (() => {
                    const all = [...(sites || []), ...(archivedSites || [])];
                    const site = all.find((s: any) => s._id === selectedSite);
                    if (!site) {
                      return (
                        <div className="loading">
                          <div className="spinner"></div>
                          Loading newly created site...
                        </div>
                      );
                    }
                    return (
                      <SiteDetailsPanel
                        site={site}
                        onClose={() => {
                          setSelectedSite(null);
                          cancelGeofenceEdit();
                        }}
                      />
                    );
                  })()
                ) : (
                  <>
                    <div className="tt-tours-toolbar sites-toolbar dispatches-toolbar">
                      <div className="tt-tours-toolbar-row dispatches-toolbar-row">
                        <div className="dispatches-search-shell">
                          <div className="dispatches-search-input-wrap">
                            <input
                              className="dispatches-search-input"
                              type="text"
                              placeholder="Search sites..."
                              value={siteSearchQuery}
                              onChange={(e) => setSiteSearchQuery(e.target.value)}
                            />
                          </div>
                          <div className="dispatches-search-divider" aria-hidden="true" />
                          <div className="dispatches-status-wrap site-type-dropdown">
                            <SiteTypeDropdown
                              value={siteTypeFilter}
                              onChange={(type) => {
                                setSiteTypeFilter(type);
                                if (type === "archived") {
                                  setSelectedSite(null);
                                  setIsCreatingSite(false);
                                  if (!isRouteConfirmed) setMapSelectedSiteId(null);
                                }
                              }}
                            />
                          </div>
                        </div>
                        {siteTypeFilter !== "archived" ? (
                          <button
                            type="button"
                            className="tt-action-btn site-create-btn"
                            onClick={() => {
                              setSelectedSite(null);
                              setIsCreatingSite(true);
                            }}
                            title="Create new site"
                            aria-label="Create new site"
                          >
                            +
                          </button>
                        ) : null}
                      </div>
                    </div>
                    
                    <div className="sites-list-container">
                      {(siteTypeFilter === "archived" ? archivedSites : sites) ? (
                        (() => {
                          const isArchiveMode = siteTypeFilter === "archived";
                          const base = isArchiveMode ? (archivedSites || []) : (sites || []);
                          const filtered = base.filter((site: any) => {
                            const normalizedSiteType = normalizeSiteTypeLabel(site.siteType);
                            const matchesSearch =
                              siteSearchQuery === '' ||
                              site.name.toLowerCase().includes(siteSearchQuery.toLowerCase()) ||
                              site.address?.toLowerCase().includes(siteSearchQuery.toLowerCase());
                            if (!matchesSearch) return false;
                            if (isArchiveMode) return true;
                            return siteTypeFilter === 'all' || normalizedSiteType === siteTypeFilter;
                          });
                          
                          if (filtered.length === 0) {
                            return <div className="empty-state">{siteTypeFilter === "archived" ? "No archived sites." : "No sites found"}</div>;
                          }
                          
                          return filtered.map((site: any) => (
                    <div
                      key={site._id}
                              className={`site-item ${selectedSite === site._id ? 'selected' : ''}`}
                      onClick={() => handleSiteSelect(site._id, { highlightOnMap: siteTypeFilter !== "archived" })}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setSiteContextMenu({
                          x: e.clientX,
                          y: e.clientY,
                          siteId: site._id,
                          mode: siteTypeFilter === "archived" ? "archived" : "active",
                        });
                      }}
                    >
                              {isSupportedSiteType(site.siteType) || normalizeSiteTypeLabel(site.siteType) ? (
                                <span className="site-item-type">{normalizeSiteTypeLabel(site.siteType)}</span>
                              ) : null}
                              <div className="site-item-name">{site.name}</div>
                              <div className="site-item-address">{site.address?.replace(/\s*\d{5}(-\d{4})?$/, '')}</div>
                      </div>
                          ));
                        })()
                ) : (
                  <div className="loading">
                    <div className="spinner"></div>
                    Loading sites...
                  </div>
                      )}
                    </div>
                    {siteContextMenu && typeof document !== "undefined"
                      ? createPortal(
                          <div
                            className="site-context-menu"
                            style={{ top: siteContextMenu.y, left: siteContextMenu.x }}
                            role="menu"
                            aria-label="Site actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {siteContextMenu.mode === "active" ? (
                              <button
                                type="button"
                                className="site-context-item danger"
                                onClick={async () => {
                                  const id = siteContextMenu.siteId;
                                  setSiteContextMenu(null);
                                  try {
                                    await archiveSite({ siteId: id as any });
                                    // If we just archived the selected site, close it.
                                    if (selectedSite === id) setSelectedSite(null);
                                    if (mapSelectedSiteId === id) setMapSelectedSiteId(null);
                                  } catch (e) {
                                    console.error("Archive failed", e);
                                  }
                                }}
                              >
                                Archive site
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="site-context-item"
                                onClick={async () => {
                                  const id = siteContextMenu.siteId;
                                  setSiteContextMenu(null);
                                  try {
                                    await unarchiveSite({ siteId: id as any });
                                  } catch (e) {
                                    console.error("Unarchive failed", e);
                                  }
                                }}
                              >
                                Unarchive site
                              </button>
                            )}
                          </div>,
                          document.body
                        )
                      : null}
                  </>
                )}
                </div>
              </SidebarPanel>
            )}

            {activeTab === 'patrols' && (
              <SidebarPanel>
              <div className="patrols-panel">
                {/* Filter Bar */}
                <div className="tt-tours-toolbar patrol-toolbar">
                  <div className="tt-tours-toolbar-row tt-row-start">
                    <input
                      className="tt-search patrol-search"
                      type="text"
                      placeholder="Search for a vehicle, guard or site..."
                      value={patrolSearchQuery}
                      onChange={(e) => setPatrolSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                {patrolVisitsData ? (
                  (() => {
                    const q = patrolSearchQuery.trim().toLowerCase();
                    const filteredVisits = q
                      ? patrolVisitsData.filter((visit: any) => {
                          const vehicle = (visit.vehicleName || '').toLowerCase();
                          const guard = (visit.guardName || '').toLowerCase();
                          const site = (visit.siteName || '').toLowerCase();
                          return vehicle.includes(q) || guard.includes(q) || site.includes(q);
                        })
                      : patrolVisitsData;

                    // Always show most recent first.
                    const sortedVisits = [...filteredVisits].sort((a: any, b: any) => b.entryTime - a.entryTime);
                    
                    return sortedVisits.length > 0 ? (
                    <>
                      {sortedVisits.map((visit: any) => {
                      const entryDate = new Date(visit.entryTime);
                      const exitDate = visit.exitTime ? new Date(visit.exitTime) : null;
                        
                        // Calculate duration from entry/exit times (more reliable than stored duration)
                        const durationMs = exitDate ? visit.exitTime - visit.entryTime : null;
                        const duration = durationMs !== null
                          ? `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000) / 1000)}s`
                          : 'In Progress';
                      
                      // Format dates
                      const entryTimeStr = entryDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                      const entryDateStr = entryDate.toLocaleDateString();
                      const exitTimeStr = exitDate ? exitDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : null;
                      
                      return (
                        <div key={visit._id} className="patrol-card">
                          <div className="patrol-card-top">
                            <div className="patrol-officer-name">
                              {formatPatrolCardLabel(visit.vehicleName, visit.guardName)}
                            </div>
                            <div className={`patrol-status ${visit.status === 'active' ? 'active' : 'completed'}`}>
                              <span className="patrol-status-dot" aria-hidden="true" />
                              {visit.status === 'active' ? 'ACTIVE' : 'COMPLETED'}
                            </div>
                          </div>

                          <div className="patrol-card-divider" />

                          <div className="patrol-card-body">
                            <div className="patrol-field">
                              <div className="patrol-field-label">Location</div>
                              <div className="patrol-field-value">{visit.siteName || 'Unknown Site'}</div>
                            </div>

                            <div className="patrol-field">
                              <div className="patrol-field-label">Entry</div>
                              <div className="patrol-field-value">{entryTimeStr}</div>
                            </div>

                            {exitDate && exitTimeStr ? (
                              <div className="patrol-field">
                                <div className="patrol-field-label">Exit</div>
                                <div className="patrol-field-value">{exitTimeStr}</div>
                              </div>
                            ) : null}
                          </div>

                          <div className="patrol-footer">
                            <div className="patrol-duration">
                              <span className="patrol-field-label patrol-duration-label">Duration</span>
                              <span className="patrol-duration-value">{duration}</span>
                            </div>

                            <div className="patrol-footer-right">
                              <div className="patrol-date">{entryDateStr}</div>
                              <button
                                type="button"
                                className="patrol-replay-btn"
                                onClick={() => handleReplayPatrol(visit)}
                                title="Replay patrol route"
                              >
                                <span className="patrol-replay-icon" aria-hidden="true" />
                                Replay
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                      
                      {/* Load More / Pagination */}
                      {patrolVisitsData.length >= patrolQueryLimit && (
                        <button 
                          type="button"
                          className="load-more-btn"
                          onClick={() => {
                            if (contentBodyRef.current) {
                              patrolScrollRestoreTopRef.current = contentBodyRef.current.scrollTop;
                            }
                            setPatrolDisplayLimit((prev) => prev + patrolLoadStep);
                          }}
                        >
                          Load More Patrols
                        </button>
                      )}
                      
                      <div className="patrol-count">
                        {`Showing ${sortedVisits.length} most recent patrols`}
                        </div>
                    </>
                  ) : (
                    <div className="home-events-empty" style={{ textAlign: 'center', padding: '20px' }}>
                      No patrol visits found
                    </div>
                  );
                  })()
                ) : (
                  <div className="loading">
                    <div className="spinner"></div>
                    Loading patrol visits...
                  </div>
                )}
              </div>
              </SidebarPanel>
            )}

            {activeTab === 'tours' && (
              <SidebarPanel>
                <ToursPanel ref={toursPanelRef} onCheckpointReplay={handleReplayPatrol} />
              </SidebarPanel>
            )}

            {activeTab === 'dispatches' && (
              <SidebarPanel>
                <DispatchesPanel
                  active={(activeDispatches as any[]) || []}
                  history={(dispatchHistory as any[]) || []}
                  focusedDispatchId={focusedDispatchId}
                  onFocusDispatch={(id: any) => {
                    setFocusedDispatchId(id);
                    const all = [...((activeDispatches as any[]) || []), ...((dispatchHistory as any[]) || [])];
                    const d = all.find((x: any) => x?._id === id);
                    if (d?.vehicleDocId) setSelectedVehicle(String(d.vehicleDocId));
                    if (d?.siteId) setMapSelectedSiteId(String(d.siteId));
                  }}
                  onReplayDispatch={(id: any) => setReplayDispatchId(id)}
                  onDeleteReplayData={async (id: any) => {
                    try {
                      const res: any = await deleteDispatchReplayData({ dispatchId: id });
                      if (replayDispatchId && String(replayDispatchId) === String(id)) setReplayDispatchId(null);
                      if (focusedDispatchId && String(focusedDispatchId) === String(id)) {
                        setFocusedDispatchId(null);
                        setIsDispatchOverlaySuppressed(false);
                        setIsRouteConfirmed(false);
                        setDispatchStartedAt(null);
                        setIsDispatchMenuOpen(false);
                        setDispatchResult(null);
                        setMapSelectedSiteId(null);
                        setActiveDispatchSiteId(null);
                        setDispatchOptionIndex(0);
                      }
                      window.alert(
                        `Deleted dispatch and ${res?.deletedPoints ?? 0} replay points from the database.`
                      );
                    } catch (e) {
                      console.error("Delete dispatch replay data failed", e);
                      window.alert("Failed to delete replay data. Check console for details.");
                    }
                  }}
                  onCancelDispatch={async (id: any) => {
                    try {
                      await cancelDispatch({ dispatchId: id });
                    } catch (e) {
                      console.error("Cancel dispatch failed", e);
                    }
                  }}
                />
              </SidebarPanel>
            )}

            {activeTab === 'alerts' && (
              <SidebarPanel>
                <div className="alerts-panel">
                  <div className="tt-tours-toolbar dispatches-toolbar events-center-toolbar">
                    <div className="tt-tours-toolbar-row dispatches-toolbar-row">
                      <div className="dispatches-search-shell">
                        <div className="dispatches-search-input-wrap">
                          <input
                            className="dispatches-search-input"
                            type="text"
                            placeholder="Search by site or guard..."
                            value={eventsCenterSearchInput}
                            onChange={(e) => setEventsCenterSearchInput(e.target.value)}
                          />
                        </div>
                        <div className="dispatches-search-divider" aria-hidden="true" />
                        <div className="events-center-filter-wrap">
                          <EventTypeDropdown
                            value={eventsCenterTypeFilter}
                            onChange={setEventsCenterTypeFilter}
                            options={EVENTS_CENTER_EVENT_TYPE_OPTIONS}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <section className="home-events-center--tab" aria-label="Events Center">
                    {eventsCenterDisplayLoading ? (
                      <div className="home-events-empty">Loading recent events…</div>
                    ) : eventsCenterEvents.length === 0 ? (
                      <div className="home-events-empty">No events found.</div>
                    ) : (
                      <>
                        <div className="home-events-list">
                          {eventsCenterEvents.map(renderHomeEventCard)}
                        </div>
                        {eventsCenterEvents.length >= eventsCenterLimit ? (
                          <button
                            type="button"
                            className="load-more-btn"
                            onClick={() => setEventsCenterLimit((prev) => prev + 100)}
                          >
                            Load 100 more
                          </button>
                        ) : null}
                        <div className="patrol-count">{`Loaded ${eventsCenterEvents.length} events`}</div>
                      </>
                    )}
                  </section>
                </div>
              </SidebarPanel>
            )}

            {activeTab === 'analytics' && (
              <SidebarPanel>
                <div className="analytics-panel">
                  <Analytics
                    onHeaderStateChange={handleAnalyticsHeaderStateChange}
                    onQuickPatrolReplay={handleReplayPatrol}
                    sidebarBackRequestNonce={analyticsSidebarBackNonce}
                  />
                </div>
              </SidebarPanel>
            )}

            {activeTab === 'settings' && (
              <SidebarPanel>
              <div className="settings-panel">
                <div className="settings-manage-accounts-wrap">
                  <button
                    type="button"
                    className="settings-manage-accounts-btn"
                    onClick={() => {
                      setManageAccountsPanel('members');
                      setManageAccountsError(null);
                      setShowManageAccountsModal(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.98 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                    <span>Manage Accounts</span>
                  </button>
                </div>
                <div className="settings-section-label">Map Layers</div>
                <div className="settings-layer-controls">
                  <button 
                    className={`settings-layer-btn ${!hideSites ? 'active' : ''}`}
                    onClick={() => setHideSites(!hideSites)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <span>Sites</span>
                    <div className={`settings-layer-indicator ${!hideSites ? 'on' : 'off'}`}>
                      {!hideSites ? 'ON' : 'OFF'}
                    </div>
                  </button>
                  
                  <button 
                    className={`settings-layer-btn ${showGeofences ? 'active' : ''}`}
                    onClick={() => setShowGeofences(!showGeofences)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span>Geofences</span>
                    <div className={`settings-layer-indicator ${showGeofences ? 'on' : 'off'}`}>
                      {showGeofences ? 'ON' : 'OFF'}
                    </div>
                  </button>

                  <button
                    className={`settings-layer-btn ${geofenceEditorEnabled ? 'active' : ''}`}
                    onClick={() => {
                      if (markerSavePendingRef.current) return;
                      setMarkerEditorEnabled(false);
                      cancelMarkerEdit();
                      const next = !geofenceEditorEnabled;
                      setGeofenceEditorEnabled(next);
                      if (next) setShowGeofences(true);
                      if (!next) {
                        cancelGeofenceEdit();
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.83H5v-.92l9.06-9.06.92.92-9.06 9.06zM20.71 7.04c.39-.39.39-1.02 0-1.41L18.37 3.29c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                    <span>Geofence editor</span>
                    <div className={`settings-layer-indicator ${geofenceEditorEnabled ? 'on' : 'off'}`}>
                      {geofenceEditorEnabled ? 'ON' : 'OFF'}
                    </div>
                  </button>

                  <button className={`settings-layer-btn ${markerEditorEnabled ? 'active' : ''}`}
                    disabled={markerEditSaving}
                    onClick={() => {
                      setMarkerEditorEnabled(!markerEditorEnabled);
                      cancelMarkerEdit();
                      setGeofenceEditorEnabled(false);
                      cancelGeofenceEdit();
                    }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 22s8-8 8-14a8 8 0 1 0-16 0c0 6 8 14 8 14Z"/><circle cx="12" cy="8" r="3"/></svg>
                    <span>Marker editor</span>
                    <div className={`settings-layer-indicator ${markerEditorEnabled ? 'on' : 'off'}`}>{markerEditorEnabled ? 'ON' : 'OFF'}</div>
                  </button>

                  <button
                    type="button"
                    className="settings-layer-action"
                    onClick={() => setShowMapAssetsManager(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                    <span>Manage Map Assets</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="settings-layer-action-chev">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>

                </div>

                <div className="settings-section-label" style={{ marginTop: 24 }}>Vehicle Management</div>
                <VehicleAdminSettings />
              </div>
              </SidebarPanel>
            )}
          </div>
        </div>
        </div>
        ) : null}

        <div className={`main-content ${isSidebarCollapsed || isFullscreenMode ? 'sidebar-collapsed' : ''}`}>
          {isReportsMode || isAssignmentsMode ? (
            <ReportsPage mode={isAssignmentsMode ? 'assignments' : 'reports'} />
          ) : (
          <>
          {/* Map Search (top-middle overlay): enter site name and press Enter */}
          {!geofenceEditorEnabled && !markerEditorEnabled && (
            <div className="map-search-overlay" role="search" aria-label="Dispatch search">
            <div className="map-search-bar">
              <div className="map-search-input-wrap">
                {tabSuggestion ? (
                  <div className="map-search-ghost" aria-hidden="true">
                    {tabSuggestion}
                  </div>
                ) : null}
                <input
                  ref={mapSearchInputRef}
                  className="map-search-input"
                  type="text"
                  placeholder="Search site…"
                  value={mapSiteSearch}
                  onChange={(e) => {
                    setMapSiteSearch(e.target.value);
                    if (mapDispatchError) setMapDispatchError(null);
                  }}
                  onFocus={() => {
                    if (mapSiteDropdownCloseTimerRef.current) {
                      window.clearTimeout(mapSiteDropdownCloseTimerRef.current);
                      mapSiteDropdownCloseTimerRef.current = null;
                    }
                    setIsMapSiteDropdownOpen(true);
                  }}
                  onBlur={() => {
                    // Delay close to allow option click
                    mapSiteDropdownCloseTimerRef.current = window.setTimeout(() => {
                      setIsMapSiteDropdownOpen(false);
                      mapSiteDropdownCloseTimerRef.current = null;
                      if (mapSearchMode === 'rspndr') {
                        setMapSearchMode('sites');
                        setRspndrSuggestions([]);
                        setRspndrSearchError(null);
                      }
                    }, 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && tabSuggestion && mapSearchMode === 'sites') {
                      e.preventDefault();
                      setMapSiteSearch(tabSuggestion);
                      setIsMapSiteDropdownOpen(true);
                      // Put caret at end
                      window.setTimeout(() => {
                        const el = mapSearchInputRef.current;
                        if (!el) return;
                        const len = el.value.length;
                        el.setSelectionRange(len, len);
                      }, 0);
                      return;
                    }
                    if (e.key === 'Escape') {
                      setIsMapSiteDropdownOpen(false);
                      return;
                    }
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (mapSearchMode === 'rspndr') {
                      if (mapSiteSearch.trim().length < 3) {
                        setMapDispatchError('Type at least 3 characters');
                        return;
                      }
                      const first = rspndrSuggestions[0];
                      if (!first) {
                        setMapDispatchError('Address not found');
                        return;
                      }
                      selectRspndrPrediction(first);
                      return;
                    }
                    const site = resolveSiteFromInput(mapSiteSearch) || filteredMapSites[0];
                    if (!site) {
                      setMapDispatchError('Site not found');
                      setDispatchResult(null);
                      return;
                    }
                    setIsMapSiteDropdownOpen(false);
                    requestDispatchToSite(site);
                  }}
                  aria-label={mapSearchMode === 'rspndr' ? 'Search address' : 'Search site'}
                />
              </div>

              {mapSiteSearch.trim() ? (
                <button
                  type="button"
                  className="map-search-clear"
                  onClick={() => {
                    setMapSiteSearch('');
                    setMapDispatchError(null);
                    setDispatchResult(null);
                    setMapSelectedSiteId(null);
                    setRspndrTarget(null);
                    setRspndrSuggestions([]);
                    setRspndrSearchError(null);
                    setDispatchOptionIndex(0);
                    setIsRouteConfirmed(false);
                    setIsMapSiteDropdownOpen(true);
                  }}
                  aria-label="Clear search"
                  title="Clear"
                >
                  ×
                </button>
              ) : null}
            </div>

            {isMapSiteDropdownOpen ? (
              <div
                className="map-site-dropdown"
                role="listbox"
                aria-label={mapSearchMode === 'rspndr' ? 'Addresses' : 'Sites'}
              >
                {mapSearchMode === 'sites' ? (
                  <>
                    {mapSiteSearch.length === 0 ? (
                      <button
                        type="button"
                        className="map-site-dropdown-item rspndr-action"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setMapSearchMode('rspndr');
                          setMapSiteSearch('');
                          setRspndrSuggestions([]);
                          setRspndrSearchError(null);
                          setMapDispatchError(null);
                          setIsMapSiteDropdownOpen(true);
                          window.setTimeout(() => mapSearchInputRef.current?.focus(), 0);
                        }}
                        role="option"
                      >
                        <div className="map-site-dropdown-item-name">RSPNDR Dispatch</div>
                        <div className="map-site-dropdown-item-sub">Search any address worldwide</div>
                      </button>
                    ) : null}
                    {filteredMapSites.length ? (
                      filteredMapSites.map((s: any) => (
                        <button
                          key={s._id}
                          type="button"
                          className="map-site-dropdown-item"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setMapSearchMode('sites');
                            setMapSiteSearch(s.name);
                            setIsMapSiteDropdownOpen(false);
                            requestDispatchToSite(s);
                          }}
                          role="option"
                        >
                          <div className="map-site-dropdown-item-name">{s.name}</div>
                          {s.address ? (
                            <div className="map-site-dropdown-item-sub">{String(s.address).replace(/\s*\d{5}(-\d{4})?$/, '')}</div>
                          ) : null}
                        </button>
                      ))
                    ) : (
                      <div className="map-site-dropdown-empty">No matching sites</div>
                    )}
                  </>
                ) : (
                  <>
                    {mapSiteSearch.length === 0 ? (
                      <button
                        type="button"
                        className="map-site-dropdown-item rspndr-action"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setMapSearchMode('sites');
                          setMapSiteSearch('');
                          setRspndrSuggestions([]);
                          setRspndrSearchError(null);
                          setMapDispatchError(null);
                          setIsMapSiteDropdownOpen(true);
                          window.setTimeout(() => mapSearchInputRef.current?.focus(), 0);
                        }}
                        role="option"
                      >
                        <div className="map-site-dropdown-item-name">Back to site search</div>
                        <div className="map-site-dropdown-item-sub">Return to saved sites</div>
                      </button>
                    ) : null}
                    {mapSiteSearch.trim().length < 3 ? (
                      <div className="map-site-dropdown-empty">Type at least 3 characters to search addresses</div>
                    ) : rspndrSearchError ? (
                      <div className="map-site-dropdown-empty">{rspndrSearchError}</div>
                    ) : rspndrSuggestions.length ? (
                      rspndrSuggestions.map((p) => (
                        <button
                          key={p.placeId || p.description}
                          type="button"
                          className="map-site-dropdown-item"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectRspndrPrediction(p)}
                          role="option"
                        >
                          <div className="map-site-dropdown-item-name">
                            {p.mainText || p.description}
                          </div>
                          {p.secondaryText ? (
                            <div className="map-site-dropdown-item-sub">{p.secondaryText}</div>
                          ) : null}
                        </button>
                      ))
                    ) : (
                      <div className="map-site-dropdown-empty">No matching addresses</div>
                    )}
                  </>
                )}
              </div>
            ) : null}

            {isDispatching ? (
              <div className="map-dispatch-card" aria-live="polite">
                Finding closest vehicle…
              </div>
            ) : dispatchResult?.optimalVehicle && !isRouteConfirmed ? (
              <div className="map-dispatch-card" aria-live="polite">
                <div className="map-dispatch-vehicle-row">
                  <div className="map-dispatch-vehicle-header" aria-label="Selected vehicle">
                    {formatDispatchVehicleLabel(
                      currentDispatchOption?.vehicleName || dispatchResult.optimalVehicle.vehicleName,
                      currentDispatchOption?.driverName || dispatchResult.optimalVehicle.driverName
                    )}
                  </div>
                  {currentDispatchOptions.length ? (
                    <div className="map-dispatch-count-badge" aria-label="Dispatch option index">
                      {safeDispatchIndex + 1} / {currentDispatchOptions.length}
                    </div>
                  ) : null}
                </div>

                <div className="map-dispatch-destination">
                  <div className="map-dispatch-destination-k">Destination</div>
                  <div className="map-dispatch-destination-v">
                    {destinationTitle}
                  </div>
                  {destinationSubtitle ? (
                    <div className="map-dispatch-destination-sub">
                      {destinationSubtitle}
                    </div>
                  ) : null}
                </div>

                <div className="map-dispatch-subrow">
                  <div className="map-dispatch-status" aria-live="polite">
                    {isRecalculatingRoute ? (recalculatingLabel || 'Updating…') : null}
                  </div>
                </div>

                <div className="map-dispatch-stats">
                  <div className="map-dispatch-stat">
                    <div className="map-dispatch-stat-k">ETA</div>
                    <div className="map-dispatch-stat-v">
                      {currentDispatchOption?.eta || dispatchResult.optimalVehicle.eta}
                    </div>
                  </div>
                  <div className="map-dispatch-stat">
                    <div className="map-dispatch-stat-k">Time</div>
                    <div className="map-dispatch-stat-v">
                      {currentDispatchOption?.duration || dispatchResult.optimalVehicle.duration}
                    </div>
                  </div>
                  <div className="map-dispatch-stat">
                    <div className="map-dispatch-stat-k">Distance</div>
                    <div className="map-dispatch-stat-v">
                      {currentDispatchOption?.distance || dispatchResult.optimalVehicle.distance}
                    </div>
                  </div>
                </div>

                <div className="map-dispatch-divider" role="separator" aria-hidden="true" />
                <div className="map-dispatch-nextprev">
                  <button
                    type="button"
                    className="map-dispatch-btn"
                    disabled={!canCycleDispatchOptions}
                    onClick={() => selectDispatchOptionIndex(safeDispatchIndex - 1)}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="map-dispatch-btn"
                    disabled={!canCycleDispatchOptions}
                    onClick={() => selectDispatchOptionIndex(safeDispatchIndex + 1)}
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    className="map-dispatch-btn"
                    disabled={isRecalculatingRoute}
                    onClick={async () => {
                      // Close sidebar by default when routing a vehicle.
                      setIsSidebarCollapsed(true);
                      // Clear the search bar once dispatch is confirmed.
                      setMapSiteSearch('');
                      setIsMapSiteDropdownOpen(false);
                      try {
                        const vehicleId = dispatchResult?.optimalVehicle?.vehicleId as any;
                        if (!vehicleId) throw new Error('[DISP-UI-START-002] Missing vehicle');
                        const routeCoordinates = (dispatchResult?.optimalVehicle?.route?.coordinates ||
                          dispatchResult?.optimalVehicle?.route?.route?.coordinates) as any;
                        let res: any = null;
                        if (isRspndrDispatch) {
                          const target =
                            rspndrTarget ||
                            (dispatchResult
                              ? {
                                  address: dispatchResult?.targetAddress || dispatchResult?.siteName || 'RSPNDR Dispatch',
                                  lat: dispatchResult?.targetLatitude,
                                  lng: dispatchResult?.targetLongitude,
                                }
                              : null);
                          if (!target || typeof target.lat !== 'number' || typeof target.lng !== 'number') {
                            throw new Error('[DISP-UI-START-003] Missing destination address');
                          }
                          res = await startRspndrDispatch({
                            vehicleDocId: vehicleId,
                            targetAddress: target.address,
                            targetLatitude: target.lat,
                            targetLongitude: target.lng,
                            routeCoordinates,
                            routeEta: dispatchResult?.optimalVehicle?.eta,
                            routeDurationSeconds:
                              dispatchResult?.optimalVehicle?._durationSeconds ??
                              dispatchResult?.optimalVehicle?.route?.legs?.[0]?.duration?.value,
                            routeDurationText: dispatchResult?.optimalVehicle?.duration,
                            routeDistanceText: dispatchResult?.optimalVehicle?.distance,
                          });
                        } else {
                          const siteId = (activeDispatchSiteId || mapSelectedSiteId) as any;
                          if (!siteId) throw new Error('[DISP-UI-START-001] Missing destination site');
                          res = await startDispatch({
                            vehicleDocId: vehicleId,
                            siteId,
                            routeCoordinates,
                            routeEta: dispatchResult?.optimalVehicle?.eta,
                            routeDurationSeconds:
                              dispatchResult?.optimalVehicle?._durationSeconds ??
                              dispatchResult?.optimalVehicle?.route?.legs?.[0]?.duration?.value,
                            routeDurationText: dispatchResult?.optimalVehicle?.duration,
                            routeDistanceText: dispatchResult?.optimalVehicle?.distance,
                          });
                        }
                        if (res?.dispatchId) setFocusedDispatchId(res.dispatchId);
                        setIsDispatchOverlaySuppressed(false);
                        setIsRouteConfirmed(true);
                        setDispatchStartedAt(Date.now());
                      } catch (e: any) {
                        console.error('Start dispatch failed', e);
                        setMapDispatchError(e?.message || 'Failed to start dispatch');
                        // keep isRouteConfirmed false
                        return;
                      }
                      setIsDispatchMenuOpen(false);
                    }}
                  >
                    Okay
                  </button>
                </div>
              </div>
            ) : mapDispatchError ? (
              <div className="map-dispatch-card map-dispatch-error" aria-live="polite">
                {mapDispatchError}
              </div>
            ) : null}
          </div>
          )}

          {/* Right-edge dispatch menu (hamburger) - available when routing OR background dispatches exist */}
          {(isRouteConfirmed && dispatchResult?.optimalVehicle) || backgroundDispatches.length ? (
            <div className="dispatch-fab-wrap" ref={dispatchMenuRef}>
              <button
                type="button"
                className="dispatch-fab"
                aria-label="Dispatch menu"
                title="Dispatch menu"
                onClick={() => setIsDispatchMenuOpen((v) => !v)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 7h16" />
                  <path d="M4 12h16" />
                  <path d="M4 17h16" />
                </svg>
                {backgroundDispatches.length ? (
                  <span className="dispatch-fab-badge" aria-label={`Background dispatches: ${backgroundDispatches.length}`}>
                    {backgroundDispatches.length}
                  </span>
                ) : null}
              </button>
              {isDispatchMenuOpen ? (
                <div className="dispatch-fab-menu" role="menu" aria-label="Dispatch actions">
                  {isRouteConfirmed && dispatchResult?.optimalVehicle ? (
                    <>
                      <button
                        type="button"
                        className="dispatch-fab-menu-btn danger"
                        role="menuitem"
                        onClick={async () => {
                          try {
                            if (focusedDispatchId) {
                              await cancelDispatch({ dispatchId: focusedDispatchId });
                            }
                          } catch (e) {
                            console.error("Cancel dispatch failed", e);
                          } finally {
                            endActiveDispatch('cancelled');
                          }
                        }}
                      >
                        Cancel Dispatch
                      </button>
                      <button
                        type="button"
                        className="dispatch-fab-menu-btn"
                        role="menuitem"
                        onClick={() => {
                          backgroundCurrentDispatch();
                          setIsDispatchMenuOpen(false);
                        }}
                      >
                        Move to background
                      </button>
                    </>
                  ) : null}

                  {backgroundDispatches.length ? (
                    <div className="dispatch-fab-section" aria-label="Background dispatches">
                      <div className="dispatch-fab-section-title">Background dispatches</div>
                      <div className="dispatch-fab-bg-list">
                        {[...backgroundDispatches].slice().reverse().map((d) => (
                          <div className="dispatch-fab-bg-row" key={d.id}>
                            <button
                              type="button"
                              className="dispatch-fab-bg-btn"
                              role="menuitem"
                              onClick={() => switchToBackgroundDispatch(d.id)}
                              title={`Switch to: ${d.dispatchType === 'rspndr' ? 'RSPNDR Dispatch — ' : ''}${d.siteName}`}
                            >
                              <div className="dispatch-fab-bg-site">
                                {d.dispatchType === 'rspndr' ? 'RSPNDR Dispatch — ' : ''}
                                {d.siteName}
                              </div>
                              <div className="dispatch-fab-bg-vehicle">
                                {formatDispatchVehicleLabel(d.vehicleName, d.driverName)}
                              </div>
                            </button>
                            <button
                              type="button"
                              className="dispatch-fab-bg-remove"
                              aria-label="Remove background dispatch"
                              title="Remove"
                              onClick={() => removeBackgroundDispatch(d.id)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {markerEditorEnabled && <>
            <div className={`geofence-editor-top-bar ${markerEditDraft ? 'editing' : 'waiting'}`}>
              <label className="marker-editor-picker">
                <span>{markerEditDraft ? 'Drag the pin, then save' : 'Choose a site to move its marker'}</span>
                <select aria-label="Site marker to edit" value={markerEditDraft?.siteId ?? ''} disabled={markerEditSaving}
                  onChange={event => event.target.value ? startMarkerEdit(event.target.value) : cancelMarkerEdit()}>
                  <option value="">Select a site…</option>
                  {(sites || []).map((site: any) => <option key={site._id} value={site._id}>{site.name}</option>)}
                </select>
              </label>
              <button className="geofence-editor-close-btn" aria-label="Exit marker editor" disabled={markerEditSaving}
                onClick={() => { cancelMarkerEdit(); setMarkerEditorEnabled(false); }} />
            </div>
            <div className="geofence-editor-bottom-bar">
              <div className="geofence-editor-controls">
                <button className="geofence-control-btn" onClick={() => setMapStyleMode(mapStyleMode === 'satellite' ? 'standard' : 'satellite')}>
                  {mapStyleMode === 'satellite' ? 'Standard View' : 'Satellite View'}</button>
                <button className="geofence-control-btn save" disabled={!markerEditDraft || markerEditSaving} onClick={() => void saveMarkerEdit()}>{markerEditSaving ? 'Saving…' : 'Save'}</button>
                <button className="geofence-control-btn cancel" disabled={!markerEditDraft || markerEditSaving} onClick={cancelMarkerEdit}>Cancel</button>
              </div>
              {markerEditError && <div role="alert" className="geofence-editor-error-inline">{markerEditError}</div>}
            </div>
          </>}
          {/* Geofence editor HUD overlay */}
          {geofenceEditorEnabled ? (() => {
            const editingSite =
              (sites || []).find((s: any) => String(s?._id) === String(geofenceEditSiteId || "")) || null;
            const editingName = editingSite?.name || (geofenceEditSiteId ? "Selected site" : "");
            return (
              <>
                {/* Top status bar - Dynamic Island inspired */}
                <div 
                  className={`geofence-editor-top-bar ${geofenceEditSiteId ? 'editing' : 'waiting'}`}
                  aria-live="polite"
                >
                  <div className="geofence-editor-status">
                    <div className="geofence-editor-badge" aria-label={geofenceEditSiteId ? "Editing" : "Waiting"}>
                    </div>
                    <div className="geofence-editor-site-name">
                      {geofenceEditSiteId ? editingName : "Click a geofence or site to start"}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="geofence-editor-close-btn"
                    aria-label="Exit geofence editor"
                    onClick={() => {
                      cancelGeofenceEdit();
                      setGeofenceEditorEnabled(false);
                    }}
                  >
                  </button>
                </div>

                {/* Bottom controls bar */}
                <div className="geofence-editor-bottom-bar">
                  <div className="geofence-editor-controls">
                    <button
                      type="button"
                      className={`geofence-control-btn ${mapStyleMode === "satellite" ? "active" : ""}`}
                      onClick={() => setMapStyleMode(mapStyleMode === "satellite" ? "standard" : "satellite")}
                    >
                      {mapStyleMode === "satellite" ? "Standard View" : "Satellite View"}
                    </button>

                    <button
                      type="button"
                      className={`geofence-control-btn ${!hideSites ? "active" : ""}`}
                      onClick={() => setHideSites((v) => !v)}
                    >
                      {!hideSites ? "Hide Sites" : "Show Sites"}
                    </button>

                    <div className="geofence-editor-divider"></div>

                    <button
                      type="button"
                      className="geofence-control-btn save"
                      onClick={() => saveGeofenceEdit()}
                      disabled={!geofenceEditSiteId}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="geofence-control-btn cancel"
                      onClick={() => cancelGeofenceEdit()}
                      disabled={!geofenceEditSiteId}
                    >
                      Cancel
                    </button>
                  </div>
                  {geofenceSaveError ? (
                    <div className="geofence-editor-error-inline">{geofenceSaveError}</div>
                  ) : null}
                </div>
              </>
            );
          })() : null}

          {dispatchResult?.optimalVehicle && isRouteConfirmed ? (
            <>
              <div className="route-header-overlay" aria-live="polite">
                <div className="route-header-card">
                  <div className="route-header-vehicle">
                    {formatDispatchVehicleLabel(
                      currentDispatchOption?.vehicleName || dispatchResult.optimalVehicle.vehicleName,
                      currentDispatchOption?.driverName || dispatchResult.optimalVehicle.driverName
                    )}
                  </div>
                  <div className="route-header-destination">
                    <span className="route-header-destination-k">To</span>
                    <span className="route-header-destination-v">
                      {destinationTitle}
                    </span>
                  </div>
                </div>
              </div>

              <div className="route-stats-overlay" aria-live="polite">
                <div className="route-stats-card">
                  <div className="map-dispatch-stats">
                    <div className="map-dispatch-stat">
                      <div className="map-dispatch-stat-k">ETA</div>
                      <div className="map-dispatch-stat-v">
                        {currentDispatchOption?.eta || dispatchResult.optimalVehicle.eta}
                      </div>
                    </div>
                    <div className="map-dispatch-stat">
                      <div className="map-dispatch-stat-k">Time</div>
                      <div className="map-dispatch-stat-v">
                        {currentDispatchOption?.duration || dispatchResult.optimalVehicle.duration}
                      </div>
                    </div>
                    <div className="map-dispatch-stat">
                      <div className="map-dispatch-stat-k">Distance</div>
                      <div className="map-dispatch-stat-v">
                        {currentDispatchOption?.distance || dispatchResult.optimalVehicle.distance}
                      </div>
                    </div>
                    <div className="map-dispatch-stat">
                      <div className="map-dispatch-stat-k">Travel</div>
                      <div className="map-dispatch-stat-v">
                        {dispatchStartedAt ? formatElapsed(travelNowMs - dispatchStartedAt) : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}

          <Map
            bodycamFocusRequest={bodycams.focusRequest}
            bodycamPoints={bodycams.points}
            bodycamTrail={activeTab === 'bodycams' ? bodycams.trail : []}
            selectedBodycamId={activeTab === 'bodycams' ? bodycams.selectedId : null}
            onSelectBodycam={selectBodycam}
            vehicles={visibleVehiclesForMap}
            sites={sites || []}
            showSiteMarkers={!hideSites}
            markerEditDraft={markerEditDraft}
            markerEditSaving={markerEditSaving}
            onMarkerEditChange={coordinates => setMarkerEditDraft(previous => previous ? { ...previous, coordinates } : null)}
            siteMarkerSizePx={defaultSiteMarkerSizePx}
            siteMarkerSizePxByType={siteMarkerSizesByType}
            selectedVehicleId={selectedVehicle}
            selectedSiteId={mapSelectedSiteId}
            siteModelFocusRequest={siteModelFocusRequest}
            dispatchResult={isRouteConfirmed || dispatchResult?.dispatchType === 'rspndr' ? dispatchResult : null}
            rspndrDestination={rspndrTarget}
            showGeofences={effectiveShowGeofences}
            geofenceEditSiteId={geofenceEditSiteId}
            onGeofenceEditChange={handleGeofenceEditChange}
            geofenceEditorEnabled={geofenceEditorEnabled}
            onGeofencePickForEdit={startGeofenceEditForSiteId}
            mapStyleMode={mapStyleMode}
            onMapStyleModeChange={setMapStyleMode}
            panToSelection={false}
            onSelectVehicle={handleVehicleSelect}
            onSelectSite={handleSiteSelect}
            assetUrls={mapAssetUrls}
            layerThumbnails={mapLayerThumbnails}
          />
          </>
          )}
        </div>

        {/* Dispatch switch confirmation (when starting a new dispatch while routing) */}
        {showManageAccountsModal ? (
          <div
            className="modal-overlay"
            onClick={() => {
              setShowManageAccountsModal(false);
            }}
          >
            <div
              className="modal manage-accounts-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Manage accounts"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="modal-title">Manage Accounts</div>
                <button
                  type="button"
                  className="modal-close"
                  aria-label="Close"
                  onClick={() => {
                    setShowManageAccountsModal(false);
                  }}
                >
                  ✕
                </button>
              </div>

              <div className="manage-accounts-tabs" role="tablist" aria-label="Account management sections">
                <button
                  type="button"
                  className={`manage-accounts-tab ${manageAccountsPanel === 'members' ? 'active' : ''}`}
                  role="tab"
                  aria-selected={manageAccountsPanel === 'members'}
                  onClick={() => {
                    setManageAccountsPanel('members');
                    setManageAccountsError(null);
                    setManageAccountsMembersToken(null);
                  }}
                >
                  Members
                </button>
                <button
                  type="button"
                  className={`manage-accounts-tab ${manageAccountsPanel === 'profile' ? 'active' : ''}`}
                  role="tab"
                  aria-selected={manageAccountsPanel === 'profile'}
                  onClick={() => setManageAccountsPanel('profile')}
                >
                  Profile
                </button>
                <button
                  type="button"
                  className={`manage-accounts-tab ${manageAccountsPanel === 'security' ? 'active' : ''}`}
                  role="tab"
                  aria-selected={manageAccountsPanel === 'security'}
                  onClick={() => setManageAccountsPanel('security')}
                >
                  Security
                </button>
                <button
                  type="button"
                  className={`manage-accounts-tab ${manageAccountsPanel === 'sessions' ? 'active' : ''}`}
                  role="tab"
                  aria-selected={manageAccountsPanel === 'sessions'}
                  onClick={() => setManageAccountsPanel('sessions')}
                >
                  Sessions
                </button>
              </div>

              {manageAccountsError ? (
                <div className="manage-accounts-error">
                  {manageAccountsError}
                  {manageAccountsPanel === 'members' ? (
                    <button
                      type="button"
                      className="manage-accounts-retry"
                      onClick={() => {
                        setManageAccountsError(null);
                        setManageAccountsMembersToken(null);
                        setManageAccountsMembersRetryKey((value) => value + 1);
                      }}
                    >
                      Retry
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="manage-accounts-widget-shell">
                {manageAccountsPanel === 'members' ? (
                  isManageAccountsMembersLoading ? (
                    <div className="manage-accounts-loading">Checking WorkOS admin permissions…</div>
                  ) : manageAccountsMembersToken ? (
                    <UsersManagement authToken={manageAccountsMembersToken} />
                  ) : (
                    <div className="manage-accounts-loading">
                      WorkOS admin access is required before member management can load.
                    </div>
                  )
                ) : null}
                {manageAccountsPanel === 'profile' ? <UserProfile authToken={fetchWorkOsWidgetToken} /> : null}
                {manageAccountsPanel === 'security' ? <UserSecurity authToken={fetchWorkOsWidgetToken} /> : null}
                {manageAccountsPanel === 'sessions' ? <UserSessions authToken={fetchWorkOsWidgetToken} /> : null}
              </div>
            </div>
          </div>
        ) : null}

        {showDispatchSwitchModal ? (
          <div
            className="modal-overlay"
            onClick={() => {
              setShowDispatchSwitchModal(false);
              setPendingDispatchSite(null);
              setPendingRspndrTarget(null);
            }}
          >
            <div
              className="modal dispatch-switch-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Dispatch options"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="modal-title">Dispatch in progress</div>
                <button
                  type="button"
                  className="modal-close"
                  aria-label="Close"
                  onClick={() => {
                    setShowDispatchSwitchModal(false);
                    setPendingDispatchSite(null);
                    setPendingRspndrTarget(null);
                  }}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="dispatch-switch-text">
                  You already have an active dispatch. Do you want to cancel it, or put it in the background and start a new dispatch to{' '}
                  <strong>{pendingDispatchSite?.name || pendingRspndrTarget?.address || 'the selected location'}</strong>?
                </div>
                <div className="dispatch-switch-actions">
                  <button
                    type="button"
                    className="dispatch-switch-btn danger"
                    onClick={async () => {
                      const site = pendingDispatchSite;
                      const target = pendingRspndrTarget;
                      setShowDispatchSwitchModal(false);
                      setPendingDispatchSite(null);
                      setPendingRspndrTarget(null);
                      endActiveDispatch('cancelled');
                      if (site) await dispatchToSiteImpl(site);
                      if (target) await dispatchToRspndrImpl(target);
                    }}
                  >
                    Cancel current dispatch & start new
                  </button>
                  <button
                    type="button"
                    className="dispatch-switch-btn"
                    onClick={async () => {
                      const site = pendingDispatchSite;
                      const target = pendingRspndrTarget;
                      setShowDispatchSwitchModal(false);
                      setPendingDispatchSite(null);
                      setPendingRspndrTarget(null);
                      backgroundCurrentDispatch();
                      if (site) await dispatchToSiteImpl(site);
                      if (target) await dispatchToRspndrImpl(target);
                    }}
                  >
                    Background current dispatch & start new
                  </button>
                  <button
                    type="button"
                    className="dispatch-switch-btn subtle"
                    onClick={() => {
                      setShowDispatchSwitchModal(false);
                      setPendingDispatchSite(null);
                      setPendingRspndrTarget(null);
                    }}
                  >
                    Keep current dispatch
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Modals */}
        {showPatrolLogModal && selectedVehicle && selectedSite && (
          <PatrolLogCreator
            vehicle={vehicles?.find((v: any) => v._id === selectedVehicle)}
            site={sites?.find((s: any) => s._id === selectedSite)}
            onClose={() => setShowPatrolLogModal(false)}
            onCreated={() => {
              // Patrol visits will automatically update via the useQuery hook
              console.log('Patrol visit created successfully');
            }}
          />
        )}

        {showConfigModal && (
          <div className="modal-overlay" onClick={() => setShowConfigModal(false)}>
            <div onClick={(e) => e.stopPropagation()}>
              <ConfigMigration />
            </div>
          </div>
        )}

        {showReplayModal && selectedPatrolVisit && (
          <PatrolReplayPlayer
            patrolVisit={selectedPatrolVisit}
            onClose={() => {
              setShowReplayModal(false);
              setSelectedPatrolVisit(null);
            }}
            assetUrls={appAssets ? {
              Vehicle_IsMoving: appAssets.Vehicle_IsMoving,
              Vehicle_NotMoving: appAssets.Vehicle_NotMoving,
            } : undefined}
            layerThumbnails={mapLayerThumbnails}
            siteId={sites?.find((site: { name: string; _id: string }) => site.name === selectedPatrolVisit.siteName)?._id}
            geofenceCoords={replayGeofenceCoords}
          />
        )}

        {replayDispatchId ? (
          <DispatchReplayPlayer
            dispatchId={replayDispatchId}
            onClose={() => setReplayDispatchId(null)}
            assetUrls={appAssets ? {
              Vehicle_IsMoving: appAssets.Vehicle_IsMoving,
              Vehicle_NotMoving: appAssets.Vehicle_NotMoving,
            } : undefined}
            layerThumbnails={mapLayerThumbnails}
          />
        ) : null}

        {showImageUploader && (
          <SiteImageUploader onClose={() => setShowImageUploader(false)} />
        )}

        {showMapAssetsManager && (
          <MapAssetsManager onClose={() => setShowMapAssetsManager(false)} />
        )}
      </div>
    </NotificationSystem>
  );
}

export default App; 
